import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import {
  approvePublicProjectProof,
  buildProjectUpdatedByFromAuth,
  createPublicAddressForProjectUser,
  fetchPublicUserAddresses,
  fetchProjectById,
  fetchProjectMessagesList,
  fetchProjectProofsSelect2,
  fetchProjectPublicAccess,
  fetchSignageProjectFilesList,
  hardDeleteWorkDriveFile,
  deleteProjectFileByProject,
  extractUploadedFileIds,
  postProjectMessage,
  publicWorkDriveFileDownloadUrl,
  projectAssetUrl,
  resolveProjectFileDownloadUrl,
  ProjectMessageRow,
  ProjectProofSelect2Row,
  ProjectSharedFileRow,
  reopenProject,
  SignageProjectDetail,
  updateProjectSendMessageToClient,
  fetchUsersSearchSignageTeamMembers,
  getLinkedOrderViewPath,
  type SignageDesignerSelectOption,
  pickProjectObjectId,
  renderSignagePersonOptionLabel,
  updateProjectLinkedOrderType,
  uploadSignageProjectFiles,
  updatePublicApprovedProofDetails,
  updatePublicApprovedProofOrderSummary,
  resolveApprovedProofVersionSummary,
  resolveProjectAddressOwnerId,
  type PublicUserAddress,
  unapproveProjectProofVersions,
} from "components/proejctsandquoteapicalls";
import AddAddressModal, {
  BillingShippingApprovalModal,
  type AddressFormValues,
} from "components/PublicAddressFormModel";
import { pickSyncMembershipId, syncUserEmailToDocument } from "api/salesOrderApi";
import { safeConsole } from "utils/envUtils";
import "./projectandquote-view.css";
import { getAuthCookie } from "../../utils/authCookies";
import {
  ViewIconButton,
  DeleteIconButton,
  DownloadIconButton,
  DownloadIcon,
} from "components/DatatablesActionIcons";
import ApprovedProofCard from "components/ApprovedProofCard";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import { components } from "react-select";
import RefreshIconButton from "components/RefreshIconButton";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import BrokerageAssociationStatusBadge from "components/BrokerageAssociationStatusBadge";

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;
const MONGO_OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

function asMongoObjectId(...values: unknown[]): string {
  const id = pickProjectObjectId(...values);
  return MONGO_OBJECT_ID_RE.test(id) ? id : "";
}

function quoteAssigneeMembershipId(opt: SignageDesignerSelectOption | null | undefined): string {
  if (!opt) return "";
  return asMongoObjectId(opt.membershipId, opt.value);
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function statusDisplay(status: string | undefined): string {
  if (!status) return "—";
  return status
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function normalizeConversationRoleKey(raw: string | undefined): string {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

/** Friendly license / sender label shown beside the conversation sender name. */
function formatConversationLicenseLabel(
  senderType?: string,
  projectLicenseType?: string
): string {
  const st = normalizeConversationRoleKey(senderType);
  if (st === "ADMIN" || st === "DESIGNER" || st === "DESIGN_TEAM" || st === "SIGNAGE_FIRM") {
    return "Admin";
  }

  if (st === "BROKERAGE_TEAM_MEMBER" || st === "TEAM_MEMBER" || st === "TEAMMEMBER") {
    return "Teammber";
  }
  if (st === "BROKERAGE_AGENT") {
    return "Brokerage Agent";
  }
  if (st === "AGENT") {
    const lt = normalizeConversationRoleKey(projectLicenseType);
    if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER" || lt === "TEAMMEMBER") {
      return "Teammber";
    }
    if (lt === "BROKERAGE_AGENT") return "Brokerage Agent";
    return "Agent";
  }

  const lt = normalizeConversationRoleKey(projectLicenseType);
  if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER" || lt === "TEAMMEMBER") {
    return "Teammber";
  }
  if (lt === "BROKERAGE_AGENT") return "Brokerage Agent";
  if (lt === "AGENT") return "Agent";
  if (lt === "BROKERAGE_FIRM") return "Brokerage Firm";
  if (lt === "ADMIN" || lt === "SIGNAGE_FIRM") return "Admin";

  const source = String(projectLicenseType || senderType || "User").trim();
  return source
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function isGenericConversationSenderName(name: string | undefined): boolean {
  const n = String(name || "").trim();
  if (!n || n === "—" || n === "-") return true;
  return /^(agent|team[_\s-]?member|brokerage[_\s-]?agent|brokerage[_\s-]?team[_\s-]?member|user|admin|designer|design[_\s-]?team)$/i.test(
    n
  );
}

function projectPartyDisplayName(project: SignageProjectDetail | null | undefined): {
  agentName: string;
  teamMemberName: string;
  endUserName: string;
} {
  const agentName = String(project?.agent_name || "").trim();
  const teamMemberName = String(project?.team_member_name || "").trim();
  const endUserName = [project?.first_name, project?.last_name]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  return { agentName, teamMemberName, endUserName };
}

/** Prefer API sender_name; when it is a generic role label, resolve the real agent/team member name. */
function resolveConversationSenderDisplayName(
  senderName: string | undefined,
  senderType: string | undefined,
  senderId: string | undefined,
  project: SignageProjectDetail | null | undefined
): string {
  const fromApi = String(senderName || "").trim();
  const st = normalizeConversationRoleKey(senderType);
  const isAdminSide =
    st === "ADMIN" || st === "DESIGNER" || st === "DESIGN_TEAM" || st === "SIGNAGE_FIRM";

  if (!isGenericConversationSenderName(fromApi)) return fromApi;
  if (isAdminSide) return fromApi || "Admin";

  const { agentName, teamMemberName, endUserName } = projectPartyDisplayName(project);
  const sid = String(senderId || "").trim();
  const agentId = String(project?.agent_id || "").trim();
  const teamMemberId = String((project as any)?.team_member_id || "").trim();

  if (sid && agentId && sid === agentId && agentName) return agentName;
  if (sid && teamMemberId && sid === teamMemberId && teamMemberName) return teamMemberName;

  const looksTeam =
    st.includes("TEAM") ||
    /team/i.test(fromApi) ||
    normalizeConversationRoleKey(project?.license_type).includes("TEAM");

  if (looksTeam && teamMemberName) return teamMemberName;
  if (agentName) return agentName;
  if (teamMemberName) return teamMemberName;
  if (endUserName) return endUserName;
  return fromApi || "—";
}

function resolveSharedFileUploaderDisplay(
  createdBy: { id?: string; name?: string; license_type?: string; user_type?: string } | undefined,
  project: SignageProjectDetail | null | undefined
): { name: string; role: string } {
  const rawName = String(createdBy?.name || "").trim();
  const explicitType = String(createdBy?.user_type || createdBy?.license_type || "").trim();
  const name = resolveConversationSenderDisplayName(
    rawName,
    explicitType || (isGenericConversationSenderName(rawName) ? rawName : undefined),
    createdBy?.id,
    project
  );

  const { agentName, teamMemberName } = projectPartyDisplayName(project);
  const nameLower = name.toLowerCase();
  const matchesAgent = Boolean(agentName && nameLower === agentName.toLowerCase());
  const matchesTeam = Boolean(teamMemberName && nameLower === teamMemberName.toLowerCase());
  const isGeneric = isGenericConversationSenderName(rawName);

  let roleSource = explicitType;
  if (!roleSource) {
    if (matchesTeam || (isGeneric && /team/i.test(rawName))) {
      roleSource = project?.license_type || "TEAM_MEMBER";
    } else if (matchesAgent || (isGeneric && /^agent$/i.test(rawName))) {
      roleSource = project?.license_type || "AGENT";
    } else if (isGeneric) {
      roleSource = project?.license_type || rawName;
    } else {
      roleSource = "ADMIN";
    }
  }

  const role = formatConversationLicenseLabel(
    roleSource,
    matchesAgent || matchesTeam || isGeneric ? project?.license_type : undefined
  );
  return { name: name || "—", role };
}

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex !important",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: "none",
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 30,
  }),
  option: (provided: any) => ({
    ...provided,
    whiteSpace: "normal",
    lineHeight: 1.35,
    paddingTop: 8,
    paddingBottom: 8,
  }),
};

type ShippingMethodOption = {
  id: string;
  name: string;
  description: string;
  priceLabel: string;
};

const SHIPPING_METHODS: ShippingMethodOption[] = [
  { id: "Install", name: "Install", description: "", priceLabel: "TBD" },
  { id: "customer_pickup", name: "Customer Pickup", description: "Atlanta only · Free", priceLabel: "FREE" },
  { id: "farmer_delivery", name: "Farmer Delivery", description: "Delivered by our team", priceLabel: "TBD" },
  { id: "ups_shipping", name: "UPS Shipping", description: "Prices calculated once quote is created", priceLabel: "TBD" },
];

const pickAddressId = (addr: any): string => String(addr?._id || addr?.id || "").trim();

const formatAddressSingleLine = (addr: any): string => {
  const d = (addr?.address_details || {}) as any;
  const label = String(addr?.label || d?.label || "").trim();
  const line1 = String(d.address_line_1 || d.addressLine1 || "").trim();
  const line2 = String(d.address_line_2 || d.addressLine2 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || "").trim();
  const zip = String(d.zip || "").trim();
  const addressCore = [line1, line2, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return label && addressCore ? `[${label}] ${addressCore}` : addressCore || label;
};

const toApprovalAddressPayload = (addr: PublicUserAddress | null) => {
  if (!addr) return null;
  const d = (addr.address_details || {}) as any;
  return {
    _id: String(addr._id || addr.id || "").trim(),
    label: String(addr.label || d.label || "").trim(),
    first_name: String(addr.first_name || "").trim(),
    last_name: String(addr.last_name || "").trim(),
    company: String(addr.company || "").trim(),
    default_address: Boolean(addr.default_address),
    address_details: {
      _id: String(d._id || "").trim(),
      label: String(d.label || addr.label || "").trim(),
      addressLine1: String(d.addressLine1 || d.address_line_1 || "").trim(),
      addressLine2: String(d.addressLine2 || d.address_line_2 || "").trim(),
      city: String(d.city || "").trim(),
      state: String(d.state || "").trim(),
      country: String(d.country || "").trim(),
      zip: String(d.zip || "").trim(),
      cityCode: String(d.cityCode || "").trim(),
      stateCode: String(d.stateCode || "").trim(),
      countryCode: String(d.countryCode || "").trim(),
      latitude: Number(d.latitude ?? 0),
      longitude: Number(d.longitude ?? 0),
      phone: String(d.phone || "").trim(),
    },
  };
};

type NormalizedProjectStatus = "IN_PROGRESS" | "DESIGN_APPROVED" | "QUOTED" | "QUOTE_APPROVED" | "COMPLETED";

type WorkflowConnector = "green" | "gray";

type WorkflowStageUi = {
  key: string;
  title: string;
  iconClass: string;
  /** Dot style: pq-dot-done | pq-dot-progress | pq-dot-pending | pq-dot-current-approved | pq-dot-current-quoted */
  dotClass: string;
  /** Status line under title */
  subClass: string;
  statusLabel: string;
  /** Vertical line after this step (before the next) */
  lineAfter: WorkflowConnector;
  titleMuted?: boolean;
  extra?: string;
  linkedQuote?: string;
};

function normalizeProjectStatus(status: string | undefined): NormalizedProjectStatus {
  const raw = String(status || "").trim().toUpperCase();
  if (raw === "DESIGN_APPROVED") return "DESIGN_APPROVED";
  if (raw === "QUOTE_APPROVED") return "QUOTE_APPROVED";
  if (raw === "QUOTED") return "QUOTED";
  if (raw === "COMPLETED") return "COMPLETED";
  return "IN_PROGRESS";
}

function getStatusPillClass(status: NormalizedProjectStatus): string {
  if (status === "DESIGN_APPROVED") return "pq-status-pill approved";
  if (status === "QUOTE_APPROVED") return "pq-status-pill quote-approved";
  if (status === "QUOTED") return "pq-status-pill quoted";
  if (status === "COMPLETED") return "pq-status-pill completed";
  return "pq-status-pill inprogress";
}

function formatAssignedToExtra(assignedToName?: string): string {
  const name = String(assignedToName || "").trim();
  return name ? `Assigned to: ${name}` : "";
}

function buildWorkflowStages(
  status: NormalizedProjectStatus,
  linkedQuote?: string,
  assignedToName?: string
): WorkflowStageUi[] {
  const q = String(linkedQuote || "").trim();
  const assignedExtra = formatAssignedToExtra(assignedToName);

  const baseCreated = (): WorkflowStageUi => ({
    key: "created",
    title: "Project created",
    iconClass: "ri-check-line",
    dotClass: "pq-dot-done",
    subClass: "pq-wf-sub-completed",
    statusLabel: "Completed",
    lineAfter: "green",
  });

  if (status === "COMPLETED") {
    return [
      baseCreated(),
      {
        key: "discussion",
        title: "Design discussions",
        iconClass: "ri-check-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
      },
      {
        key: "approved",
        title: "Design approved",
        iconClass: "ri-thumb-up-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
        extra: assignedExtra,
      },
      {
        key: "quoted",
        title: "Quote sent",
        iconClass: "ri-file-list-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
      },
      // {
      //   key: "completed",
      //   title: "Payment completed and Converted",
      //   iconClass: "ri-shopping-cart-line",
      //   dotClass: "pq-dot-done",
      //   subClass: "pq-wf-sub-completed",
      //   statusLabel: "Completed",
      //   lineAfter: "gray",
      // },
    ];
  }

  if (status === "QUOTE_APPROVED") {
    return [
      baseCreated(),
      {
        key: "discussion",
        title: "Design discussions",
        iconClass: "ri-check-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
      },
      {
        key: "approved",
        title: "Design approved",
        iconClass: "ri-thumb-up-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
        extra: assignedExtra,
      },
      {
        key: "quoted",
        title: "Quote approved",
        iconClass: "ri-checkbox-circle-line",
        dotClass: "pq-dot-current-approved",
        subClass: "pq-wf-sub-current-green",
        statusLabel: "Current Stage",
        lineAfter: "gray",
        linkedQuote: q,
      },
    ];
  }

  if (status === "QUOTED") {
    return [
      baseCreated(),
      {
        key: "discussion",
        title: "Design discussions",
        iconClass: "ri-check-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
      },
      {
        key: "approved",
        title: "Design approved",
        iconClass: "ri-thumb-up-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
        extra: assignedExtra,
      },
      {
        key: "quoted",
        title: "Quote sent",
        iconClass: "ri-file-list-line",
        dotClass: "pq-dot-current-quoted",
        subClass: "pq-wf-sub-current-orange",
        statusLabel: "Current Stage",
        lineAfter: "gray",
        linkedQuote: q,
      },
      // {
      //   key: "completed",
      //   title: "Payment completed and Converted",
      //   iconClass: "ri-shopping-cart-line",
      //   dotClass: "pq-dot-pending",
      //   subClass: "pq-wf-sub-pending",
      //   statusLabel: "Pending",
      //   lineAfter: "gray",
      //   titleMuted: true,
      // },
    ];
  }

  if (status === "DESIGN_APPROVED") {
    return [
      baseCreated(),
      {
        key: "discussion",
        title: "Design discussions",
        iconClass: "ri-check-line",
        dotClass: "pq-dot-done",
        subClass: "pq-wf-sub-completed",
        statusLabel: "Completed",
        lineAfter: "green",
      },
      {
        key: "approved",
        title: "Design approved",
        iconClass: "ri-thumb-up-line",
        dotClass: "pq-dot-current-approved",
        subClass: "pq-wf-sub-current-green",
        statusLabel: "Current Stage",
        lineAfter: "gray",
        extra: assignedExtra,
      },
      {
        key: "quoted",
        title: "Quote sent",
        iconClass: "ri-file-list-line",
        dotClass: "pq-dot-pending",
        subClass: "pq-wf-sub-pending",
        statusLabel: "Pending",
        lineAfter: "gray",
        titleMuted: true,
      },
      // {
      //   key: "completed",
      //   title: "Payment completed and Converted",
      //   iconClass: "ri-shopping-cart-line",
      //   dotClass: "pq-dot-pending",
      //   subClass: "pq-wf-sub-pending",
      //   statusLabel: "Pending",
      //   lineAfter: "gray",
      //   titleMuted: true,
      // },
    ];
  }

  /* IN_PROGRESS — step 1 done (green), step 2 active (purple), rest pending (grey) */
  return [
    baseCreated(),
    {
      key: "discussion",
      title: "Design discussions",
      iconClass: "ri-message-2-line",
      dotClass: "pq-dot-progress",
      subClass: "pq-wf-sub-progress",
      statusLabel: "In Progress",
      lineAfter: "gray",
    },
    {
      key: "approved",
      title: "Design approved",
      iconClass: "ri-thumb-up-line",
      dotClass: "pq-dot-pending",
      subClass: "pq-wf-sub-pending",
      statusLabel: "Pending",
      lineAfter: "gray",
      extra: assignedExtra,
      titleMuted: true,
    },
    {
      key: "quoted",
      title: "Quote sent",
      iconClass: "ri-file-list-line",
      dotClass: "pq-dot-pending",
      subClass: "pq-wf-sub-pending",
      statusLabel: "Pending",
      lineAfter: "gray",
      titleMuted: true,
    },
    // {
    //   key: "completed",
    //   title: "Payment completed and Converted",
    //   iconClass: "ri-shopping-cart-line",
    //   dotClass: "pq-dot-pending",
    //   subClass: "pq-wf-sub-pending",
    //   statusLabel: "Pending",
    //   lineAfter: "gray",
    //   titleMuted: true,
    // },
  ];
}

function groupProofsByVersion(
  rows: ProjectProofSelect2Row[]
): { version: number; files: ProjectProofSelect2Row[] }[] {
  const m = new Map<number, ProjectProofSelect2Row[]>();
  for (const r of rows) {
    const v = Number(r.version);
    const key = Number.isFinite(v) ? v : 0;
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(r);
  }
  return Array.from(m.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([version, files]) => ({ version, files }));
}

function flattenProofsFromFilesResponse(
  proofs: Record<string, ProjectSharedFileRow[]> | undefined
): ProjectProofSelect2Row[] {
  if (!proofs || typeof proofs !== "object") return [];
  const out: ProjectProofSelect2Row[] = [];
  Object.entries(proofs).forEach(([key, files]) => {
    const version = Number(key);
    if (!Array.isArray(files)) return;
    files.forEach((row: any) => {
      out.push({
        id: String(row?._id || row?.id || row?.file_id || ""),
        file_id: row?.file_id,
        file_name: row?.file_name,
        original_name: row?.original_name,
        file_url: row?.file_url,
        preview_url: row?.preview_url,
        file_type: row?.file_type,
        file_size: row?.file_size,
        quantity: Number.isFinite(Number(row?.quantity)) ? Math.floor(Number(row?.quantity)) : undefined,
        created_by: row?.created_by,
        created_at: row?.created_at,
        version: Number.isFinite(version) ? version : Number(row?.version),
        is_proof: row?.is_proof,
        is_approved: row?.is_approved,
      });
    });
  });
  return out;
}

function fileTypeLabel(fileType: string | undefined, fileName: string): string {
  if (fileType?.includes("pdf")) return "PDF";
  if (fileType?.includes("png")) return "PNG";
  if (fileType?.includes("jpeg") || fileType?.includes("jpg")) return "JPG";
  const ext = fileName.split(".").pop()?.toUpperCase();
  return ext || "FILE";
}

function isHttpUrl(s: string | undefined): boolean {
  return /^https?:\/\//i.test(String(s || "").trim());
}

function isApprovedFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return false;
}

type ApprovedProofMeta = {
  billingAddress?: any;
  shippingAddress?: any;
  poNumber?: string;
  quantity?: number;
  fileQuantities?: Array<{ project_file_id: string; quantity: number }>;
  shippingMethod?: { name?: string; description?: string } | null;
  bothAddressSame?: boolean;
  orderSummary?: string;
};

function buildApprovedProofMetaByVersion(
  project: SignageProjectDetail | null,
  proofRows: ProjectProofSelect2Row[]
): Map<number, ApprovedProofMeta> {
  const map = new Map<number, ApprovedProofMeta>();

  const projectAny = project as any;
  const approvedProofs = Array.isArray(projectAny?.approved_proofs) ? projectAny.approved_proofs : [];
  approvedProofs.forEach((row: any) => {
    const version = Number(row?.version);
    if (!Number.isFinite(version) || version <= 0) return;
    map.set(version, {
      billingAddress: row?.billing_address,
      shippingAddress: row?.shipping_address,
      poNumber: String(row?.po_number ?? row?.poNumber ?? "").trim(),
      quantity: Number(row?.quantity),
      fileQuantities: Array.isArray(row?.file_quantities) ? row.file_quantities : [],
      shippingMethod: row?.shipping_method || null,
      bothAddressSame: Boolean(row?.both_address_same),
      orderSummary:
        resolveApprovedProofVersionSummary(projectAny, version, row) || undefined,
    });
  });

  proofRows.forEach((row: any) => {
    const version = Number(row?.version);
    if (!Number.isFinite(version) || version <= 0) return;
    const existing = map.get(version) || {};
    map.set(version, {
      billingAddress: existing.billingAddress ?? row?.billing_address,
      shippingAddress: existing.shippingAddress ?? row?.shipping_address,
      poNumber:
        existing.poNumber ||
        String(row?.po_number ?? row?.poNumber ?? "").trim() ||
        undefined,
      quantity: Number.isFinite(Number(existing.quantity)) ? existing.quantity : Number(row?.quantity),
      fileQuantities:
        existing.fileQuantities ?? (Array.isArray((row as any)?.file_quantities) ? (row as any).file_quantities : []),
      shippingMethod: existing.shippingMethod ?? row?.shipping_method ?? null,
      bothAddressSame:
        typeof existing.bothAddressSame === "boolean"
          ? existing.bothAddressSame
          : Boolean((row as any)?.both_address_same),
      orderSummary:
        existing.orderSummary ??
        (resolveApprovedProofVersionSummary(projectAny, version, row) || undefined),
    });
  });

  return map;
}

function formatFileSizeForUi(bytes: number | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isLikelyImagePreview(fileType: string | undefined, fileName: string): boolean {
  const t = (fileType || "").toLowerCase();
  if (t.includes("svg")) return false;
  if (t.includes("image/")) return true;
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  return ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
}

/** End-user line: first + last name, optional brokerage (for proof “by …” copy). */
function projectEndUserDisplayLine(p: SignageProjectDetail | null): string {
  if (!p) return "";
  const fn = String(p.first_name ?? "").trim();
  const ln = String(p.last_name ?? "").trim();
  const name = [fn, ln].filter(Boolean).join(" ").trim();
  const brokerage = String(p.brokerage_firm_name ?? "").trim();
  if (name && brokerage) return `${name} · ${brokerage}`;
  return name || brokerage || "";
}

/** Split name / brokerage for stacked “by …” lines on narrow viewports. */
function projectEndUserBylineParts(
  p: SignageProjectDetail | null,
  alternateName?: string
): { primary: string; secondary?: string } {
  const alt = String(alternateName ?? "").trim();
  const brokerage = p ? String(p.brokerage_firm_name ?? "").trim() : "";
  const fn = p ? String(p.first_name ?? "").trim() : "";
  const ln = p ? String(p.last_name ?? "").trim() : "";
  const nameFromParts = [fn, ln].filter(Boolean).join(" ").trim();
  const agentName = p ? String(p.agent_name ?? "").trim() : "";
  const name = nameFromParts || agentName;
  if (name && brokerage) return { primary: name, secondary: brokerage };
  if (name) return { primary: name };
  if (brokerage) return { primary: brokerage };
  if (alt) return { primary: alt };
  return { primary: "Agent" };
}

function renderProofAttribution(
  project: SignageProjectDetail | null,
  alternateName?: string
): React.ReactNode {
  const { primary, secondary } = projectEndUserBylineParts(project, alternateName);
  if (secondary) {
    return (
      <div className="pq-msg-proof-by pq-msg-proof-by--stack">
        <span className="pq-msg-proof-by-line">
          by {primary}
        </span>
        <span className="pq-msg-proof-by-line pq-msg-proof-by-line--secondary">{secondary}</span>
      </div>
    );
  }
  return <span className="pq-msg-proof-by">by {primary}</span>;
}

function PqFilePreviewImage({ src, alt }: { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="pq-file-preview-fallback">
        <i className="ri-image-line" aria-hidden />
        <p>Preview not available for this file type.</p>
      </div>
    );
  }
  return (
    <img
      className="pq-file-preview-img"
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}

/** Reply file row for conversation bubbles (from API `reply_to_files`). */
type UiReplyFile = {
  id: string;
  /** Work-drive `file_id` for `/public/work-drive/files/:id/download` (not message join `id`). */
  apiFileId?: string;
  name: string;
  previewUrl: string;
  fileType?: string;
  proofLabel?: string;
  isProof?: boolean;
  version?: number;
  fileSize?: number;
  quantity?: number;
  designApprovedByName?: string;
  designApprovedAt?: string;
};

type UiMessage = {
  id: string;
  sender: string;
  role: string;
  time: string;
  text: string;
  /** Legacy placeholder; use `replyFiles` for API-backed attachments. */
  attachments: string[];
  replyFiles: UiReplyFile[];
  replyToMessageId?: string;
  /** Present when API sends `approved_by` / `design_approved_by` — proof header. */
  approvedBy?: { id?: string; name?: string };
  /** Message-level design approval timestamp when API sends `design_approved_at`. */
  designApprovedAt?: string;
  /** Proof version for approved header (from reply_to_files). */
  proofVersion?: number;
};

function inferMessageProofVersion(m: ProjectMessageRow): number {
  const files = m.reply_to_files;
  if (!Array.isArray(files) || files.length === 0) return 1;
  const vs = files
    .filter((f) => f.is_proof && typeof f.version === "number" && Number.isFinite(Number(f.version)))
    .map((f) => Number(f.version));
  if (vs.length === 0) return 1;
  return Math.max(...vs);
}

function mapReplyFilesFromApi(m: ProjectMessageRow): UiReplyFile[] {
  const raw = m.reply_to_files;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.flatMap((f, idx): UiReplyFile[] => {
    const workDriveId = String(f.file_id ?? "").trim();
    const joinId = String(f.id ?? f._id ?? "").trim();
    const hasWorkDriveId = Boolean(workDriveId && !/^0+$/.test(workDriveId));
    const hasJoinId = Boolean(joinId && !/^0+$/.test(joinId));

    let id = hasJoinId ? joinId : hasWorkDriveId ? workDriveId : "";
    if (!id) {
      const slug = (f.original_name || f.file_name || "file").replace(/\s+/g, "-").slice(0, 48);
      id = `rf-${idx}-${slug || "file"}`;
    }
    const name = (f.original_name || f.file_name || "File").trim() || "File";
    const preview = (f.preview_url || f.file_url || "").trim();
    if (!preview && !hasWorkDriveId && !hasJoinId) return [];
    const proofLabel =
      f.is_proof && typeof f.version === "number" && Number.isFinite(f.version)
        ? `Proof V${f.version}`
        : undefined;
    const dab = f.design_approved_by;
    const approverName =
      dab && typeof dab === "object" ? String(dab.name || "").trim() : "";
    const row: UiReplyFile = {
      id,
      /** Work-drive id for `/public/work-drive/files/:id/download` — not the join row id. */
      apiFileId: hasWorkDriveId ? workDriveId : undefined,
      name,
      previewUrl: preview,
      fileType: f.file_type,
      proofLabel,
      isProof: Boolean(f.is_proof),
      version: typeof f.version === "number" ? f.version : undefined,
      fileSize: typeof f.file_size === "number" ? f.file_size : undefined,
      quantity: Number.isFinite(Number((f as any).quantity)) ? Math.max(0, Math.floor(Number((f as any).quantity))) : undefined,
      designApprovedByName: approverName || undefined,
      designApprovedAt: f.design_approved_at ? String(f.design_approved_at).trim() : undefined,
    };
    return [row];
  });
}

const parseMessageId = (raw: unknown): string => {
  const r = raw as any;
  return String(
    r?.message_id ??
    r?.id ??
    r?._id ??
    r?.data?.message_id ??
    r?.data?.id ??
    r?.data?._id ??
    ""
  );
};

function pickAddrField(o: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!o) return "";
  for (const k of keys) {
    const v = o[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** Resolves address for display: project-level `address`, then office.officeAddress (object or array[0]). */
function displayAddressFromProject(project: SignageProjectDetail | null): {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
} {
  const empty = {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
  };
  if (!project) return empty;

  const direct = project.address;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    const a = direct as Record<string, unknown>;
    return {
      addressLine1: pickAddrField(a, ["addressLine1", "address_line1"]),
      addressLine2: pickAddrField(a, ["addressLine2", "address_line2"]),
      city: pickAddrField(a, ["city"]),
      state: pickAddrField(a, ["state"]),
      zip: pickAddrField(a, ["zip", "zipCode", "zip_code"]),
    };
  }

  const rawOA = project.office?.officeAddress;
  let row: Record<string, unknown> | null = null;
  if (Array.isArray(rawOA) && rawOA.length > 0) {
    const el = rawOA[0];
    if (el && typeof el === "object" && !Array.isArray(el)) row = el as Record<string, unknown>;
  } else if (rawOA && typeof rawOA === "object" && !Array.isArray(rawOA)) {
    row = rawOA as Record<string, unknown>;
  }

  if (!row) return empty;

  return {
    addressLine1: pickAddrField(row, ["addressLine1", "address_line1"]),
    addressLine2: pickAddrField(row, ["addressLine2", "address_line2"]),
    city: pickAddrField(row, ["city"]),
    state: pickAddrField(row, ["state"]),
    zip: pickAddrField(row, ["zip", "zipCode", "zip_code"]),
  };
}

const COMPOSER_EMOJIS = [
  "😊",
  "👍",
  "❤️",
  "😃",
  "🎉",
  "👏",
  "🙏",
  "✅",
  "💯",
  "🔥",
  "💪",
  "😄",
  "🤝",
  "⭐",
  "✨",
  "💼",
  "📋",
  "📄",
  "🏠",
  "🔑",
  "💰",
  "📞",
  "📩",
  "⏰",
];

function resolveProjectVendorOrganizationId(project: SignageProjectDetail | null | undefined): string {
  const p = project as Record<string, unknown> | null | undefined;
  return String(
    p?.vendor_org_id ??
      p?.organization_id ??
      p?.vendor_id ??
      p?.owner_node_id ??
      getAuthCookie("organizationId") ??
      ""
  ).trim();
}

const SignageViewprojectandquote: React.FC = () => {
  const organizationId = getAuthCookie("organizationId");
  const MESSAGES_PAGE_LIMIT = 50;
  const navigate = useNavigate();
  const location = useLocation();
  const projectsListPath = location.pathname.includes("/signageteammember")
    ? "/signageteammember/projects"
    : "/signage/projects";
  const projectsEditBase = location.pathname.includes("/signageteammember")
    ? "/signageteammember/projects"
    : "/signage/projects";

  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { projectId } = useParams<{ projectId: string }>();
  const token = getAuthCookie("sessionToken") || "";

  const [tab, setTab] = useState<"conversation" | "files">("conversation");
  const [showActions, setShowActions] = useState(false);
  const [showAgentDetails, setShowAgentDetails] = useState(true);
  const [selectedProofKey, setSelectedProofKey] = useState("");
  const [selectedOrderType, setSelectedOrderType] = useState("");
  /** Pending uploads in the composer; when non-empty, "Design Proof?" is required before Send. */
  const [attachedDraftFiles, setAttachedDraftFiles] = useState<{ id: string; file: File }[]>([]);
  const [designProofChoice, setDesignProofChoice] = useState<null | "yes" | "no">(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [project, setProject] = useState<SignageProjectDetail | null>(null);
  const [apiMessages, setApiMessages] = useState<ProjectMessageRow[]>([]);
  const [sharedFiles, setSharedFiles] = useState<ProjectSharedFileRow[]>([]);
  const [proofResults, setProofResults] = useState<ProjectProofSelect2Row[]>([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [replyToMessageId, setReplyToMessageId] = useState("");
  /** When true, backend skips the conversation email (`mute_messages_to_client`). */
  const [conversationEndUserEmailMuted, setConversationEndUserEmailMuted] = useState(false);
  const [filePreview, setFilePreview] = useState<{
    name: string;
    previewUrl: string;
    href: string;
    apiFileId?: string;
    sizeLabel: string;
    dateLabel: string;
  } | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isApprovingProof, setIsApprovingProof] = useState(false);
  const [unapprovingProofVersion, setUnapprovingProofVersion] = useState<number | null>(null);
  const [isSavingOrderType, setIsSavingOrderType] = useState(false);
  const [isReopeningProject, setIsReopeningProject] = useState(false);
  const [showReopenProjectModal, setShowReopenProjectModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddressPickerModal, setShowAddressPickerModal] = useState(false);
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [pendingApproveSelectedProof, setPendingApproveSelectedProof] = useState(false);
  const [editingApprovedProofVersion, setEditingApprovedProofVersion] = useState<number | null>(null);
  const [orderSummarySavingVersion, setOrderSummarySavingVersion] = useState<number | null>(null);
  const [publicAddresses, setPublicAddresses] = useState<PublicUserAddress[]>([]);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState("");
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState("");
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState("ups_shipping");
  const [selectedFileQuantities, setSelectedFileQuantities] = useState<Record<string, number>>({});
  const [approvalPoNumber, setApprovalPoNumber] = useState("");
  const [selectedQuoteAssigneeOption, setSelectedQuoteAssigneeOption] =
    useState<SignageDesignerSelectOption | null>(null);
  const [isSavingQuoteAssignee, setIsSavingQuoteAssignee] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
  const [deleteTargetType, setDeleteTargetType] = useState<"project" | "hard">("project");
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const actionsMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const convoListRef = useRef<HTMLDivElement | null>(null);
  const adjustPrependScrollRef = useRef<{ previousHeight: number; previousTop: number } | null>(null);
  const shouldScrollToBottomRef = useRef(false);

  const reloadProjectData = async (detailSeed?: SignageProjectDetail | null) => {
    if (!projectId || !token) return;
    const detail = detailSeed || project;
    const signageId = resolveProjectVendorOrganizationId(detail);
    const baseMessagesRes = await fetchProjectMessagesList(
      token,
      projectId,
      1,
      MESSAGES_PAGE_LIMIT
    ).catch((e) => {
        console.error(e);
        return { data: [] as ProjectMessageRow[], total: 0 };
      });
    const totalMessages = Number(baseMessagesRes.total || 0);
    const lastPage = Math.max(1, Math.ceil(totalMessages / MESSAGES_PAGE_LIMIT));
    const msgRes =
      lastPage === 1
        ? baseMessagesRes
        : await fetchProjectMessagesList(token, projectId, lastPage, MESSAGES_PAGE_LIMIT).catch((e) => {
          console.error(e);
          return { data: [] as ProjectMessageRow[], total: totalMessages };
        });

    const [filesRes, proofsRes] = await Promise.all([
      signageId
        ? fetchSignageProjectFilesList(token, signageId, projectId).catch((e) => {
          console.error(e);
          return { shared_files: [] as ProjectSharedFileRow[], proofs: undefined as Record<string, ProjectSharedFileRow[]> | undefined };
        })
        : Promise.resolve({ shared_files: [] as ProjectSharedFileRow[], proofs: undefined as Record<string, ProjectSharedFileRow[]> | undefined }),
      signageId
        ? fetchProjectProofsSelect2(token, signageId, projectId, 1, 100).catch((e) => {
          console.error(e);
          return { results: [] as ProjectProofSelect2Row[], hasMore: false };
        })
        : Promise.resolve({ results: [] as ProjectProofSelect2Row[], hasMore: false }),
    ]);
    setApiMessages(msgRes.data);
    setMessagesPage(lastPage);
    shouldScrollToBottomRef.current = true;
    setSharedFiles(filesRes.shared_files);
    const proofRowsFromFiles = flattenProofsFromFilesResponse(filesRes.proofs);
    setProofResults(proofRowsFromFiles.length > 0 ? proofRowsFromFiles : proofsRes.results);
  };

  const loadOlderMessages = useCallback(async () => {
    if (!projectId || !token) return;
    if (isLoadingOlderMessages) return;
    if (messagesPage <= 1) return;

    const listEl = convoListRef.current;
    if (listEl) {
      adjustPrependScrollRef.current = {
        previousHeight: listEl.scrollHeight,
        previousTop: listEl.scrollTop,
      };
    }

    setIsLoadingOlderMessages(true);
    try {
      const nextPage = messagesPage - 1;
      const res = await fetchProjectMessagesList(token, projectId, nextPage, MESSAGES_PAGE_LIMIT);
      setApiMessages((prev) => {
        const existingIds = new Set(
          prev.map((m) => String((m as any).id ?? (m as any)._id ?? "").trim()).filter(Boolean)
        );
        const older = (Array.isArray(res.data) ? res.data : []).filter((m) => {
          const id = String((m as any).id ?? (m as any)._id ?? "").trim();
          return id && !existingIds.has(id);
        });
        return [...older, ...prev];
      });
      setMessagesPage(nextPage);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to load older messages.");
      adjustPrependScrollRef.current = null;
    } finally {
      setIsLoadingOlderMessages(false);
    }
  }, [projectId, token, isLoadingOlderMessages, messagesPage]);

  const signageFirmID = resolveProjectVendorOrganizationId(project);
  const loadQuoteAssigneeOptions: LoadOptions<
    SignageDesignerSelectOption,
    any,
    { page: number }
  > = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      if (!token || !signageFirmID) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { options, hasMore } = await fetchUsersSearchSignageTeamMembers(
          token,
          organizationId || "",
          { page, limit: 10, search: q }
        );
        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to load team members");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, organizationId]
  );

  const resolvePublicAccessToken = useCallback(async (): Promise<string> => {
    if (!token || !projectId) return "";
    const publicAccessRes = await fetchProjectPublicAccess(token, projectId);
    const p = (publicAccessRes as any)?.data ?? (publicAccessRes as any) ?? {};
    const rawPublicUrl = String(p?.public_url || "").trim();
    const tokenFromPublicUrl = rawPublicUrl.includes("?")
      ? new URLSearchParams(rawPublicUrl.split("?")[1]).get("token") || ""
      : "";
    return String(p?.access_token || p?.accessToken || tokenFromPublicUrl || "").trim();
  }, [token, projectId]);

  const loadPublicAddresses = useCallback(async () => {
    const addressOwnerId = resolveProjectAddressOwnerId(
      project as Record<string, unknown> | null
    );
    if (!addressOwnerId) {
      setPublicAddresses([]);
      return [] as PublicUserAddress[];
    }
    try {
      const vendorOrgId = resolveProjectVendorOrganizationId(project);
      const publicAccessToken = await resolvePublicAccessToken();
      const rows = await fetchPublicUserAddresses(
        addressOwnerId,
        vendorOrgId,
        publicAccessToken
      );
      const list = Array.isArray(rows) ? rows : [];
      setPublicAddresses(list);
      return list;
    } catch {
      setPublicAddresses([]);
      return [] as PublicUserAddress[];
    }
  }, [project, resolvePublicAccessToken]);

  useEffect(() => {
    if (!projectId || !token) {
      setPageLoading(false);
      if (!token) setLoadError("Please sign in again.");
      else setLoadError("Missing project.");
      return;
    }

    let cancelled = false;
    setPageLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const detail = await fetchProjectById(token, projectId);
        if (cancelled) return;
        setProject(detail);

        await reloadProjectData(detail);
        if (cancelled) return;
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : "Failed to load project";
          setLoadError(msg);
          showError(msg);
          setProject(null);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, token]);

  const proofVersionGroups = useMemo(() => groupProofsByVersion(proofResults), [proofResults]);

  useEffect(() => {
    if (proofVersionGroups.length === 0) {
      setSelectedProofKey("");
      return;
    }
    setSelectedProofKey((prev) => {
      if (prev && proofVersionGroups.some((g) => `v-${g.version}` === prev)) return prev;
      const last = proofVersionGroups[proofVersionGroups.length - 1];
      return `v-${last.version}`;
    });
  }, [proofVersionGroups]);

  useEffect(() => {
    const row = project as SignageProjectDetail | null;
    const assignedMembershipId = asMongoObjectId(
      row?.assigned_to_membership_id,
      (row as { assigned_to_id?: string } | null)?.assigned_to_id
    );
    const assignedGlobalUserId = asMongoObjectId(row?.assigned_to_global_user_id);
    const assignedName = String(row?.assigned_to_name || "").trim();
    const assignedEmail = String(row?.assigned_to_email || row?.payment_email || "").trim();
    if (!assignedMembershipId && !assignedName) {
      setSelectedQuoteAssigneeOption(null);
      return;
    }
    setSelectedQuoteAssigneeOption({
      value: assignedMembershipId,
      membershipId: assignedMembershipId,
      globalUserId: assignedGlobalUserId,
      label: assignedName || "Assigned Member",
      firstName: "",
      lastName: "",
      email: assignedEmail,
      phone: "",
    });
  }, [project]);

  useEffect(() => {
    if (!sameAsBilling) return;
    if (!selectedBillingAddressId) return;
    setSelectedShippingAddressId(selectedBillingAddressId);
  }, [sameAsBilling, selectedBillingAddressId]);

  useEffect(() => {
    if (!project) return;
    const muteFlag = (project as any)?.mute_messages_to_client;
    const sendFlag = (project as any)?.send_message_to_client;
    // Prefer explicit mute flag (default false = not muted in UI).
    if (typeof muteFlag === "boolean") {
      setConversationEndUserEmailMuted(Boolean(muteFlag));
      return;
    }
    // Fallback: send_message_to_client true means emails ON → not muted.
    if (typeof sendFlag === "boolean") {
      setConversationEndUserEmailMuted(!Boolean(sendFlag));
    }
  }, [project]);

  const messages: UiMessage[] = useMemo(
    () =>
      [...apiMessages]
        .sort((a, b) => {
          const ta = new Date(a.created_at || 0).getTime();
          const tb = new Date(b.created_at || 0).getTime();
          return ta - tb;
        })
        .map((m, idx) => {
          const id = String(m.id ?? m._id ?? "").trim() || `msg-${idx}`;
          const ab = m.approved_by ?? m.design_approved_by;
          const approvedBy =
            ab &&
              typeof ab === "object" &&
              (String(ab.name || "").trim() || String(ab.id || "").trim())
              ? { id: String(ab.id ?? ""), name: String(ab.name ?? "").trim() }
              : undefined;
          const designApprovedAt = String(m.design_approved_at || "").trim() || undefined;

          return {
            id,
            sender: resolveConversationSenderDisplayName(
              m.sender_name,
              m.sender_type,
              m.sender_id,
              project
            ),
            role: formatConversationLicenseLabel(m.sender_type, project?.license_type),
            time: formatDateTime(m.created_at),
            text: m.message || "",
            attachments: [] as string[],
            replyFiles: mapReplyFilesFromApi(m),
            replyToMessageId: m.reply_to_message_id || "",
            approvedBy,
            designApprovedAt,
            proofVersion: inferMessageProofVersion(m),
          };
        }),
    [apiMessages, project]
  );

  const messagesById = useMemo(() => {
    const map = new Map<string, UiMessage>();
    messages.forEach((m) => map.set(m.id, m));
    return map;
  }, [messages]);

  const replyingToMessage = useMemo(
    () => (replyToMessageId ? messagesById.get(replyToMessageId) : undefined),
    [replyToMessageId, messagesById]
  );

  const agentNameParts = useMemo(() => {
    const first = (project?.first_name || "").trim();
    const last = (project?.last_name || "").trim();
    if (first || last) {
      return { first: first || "—", last: last || "—" };
    }
    const full = (project?.agent_name || project?.team_member_name || "").trim();
    if (!full) return { first: "—", last: "—" };
    const parts = full.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: "—" };
    return { first: parts[0], last: parts.slice(1).join(" ") };
  }, [project?.first_name, project?.last_name, project?.agent_name, project?.team_member_name]);

  const endUserPartyInfo = useMemo(() => {
    const zeroId = "000000000000000000000000";
    const nonZero = (v: unknown) => {
      const s = String(v ?? "").trim();
      return Boolean(s && s !== zeroId);
    };
    const licenseType = String(project?.license_type || "")
      .trim()
      .toUpperCase()
      .replace(/-/g, "_");
    const isTeamMemberLicense =
      licenseType.includes("TEAM_MEMBER") || licenseType === "TEAMMEMBER";
    const isAgentLicense =
      licenseType === "AGENT" || licenseType === "BROKERAGE_AGENT";

    const hasAgentDetails = Boolean(
      nonZero((project as any)?.agent_id) ||
        nonZero((project as any)?.agent_membership_id) ||
        nonZero((project as any)?.agent_global_user_id) ||
        String(project?.agent_name || "").trim()
    );
    const hasTeamMemberDetails = Boolean(
      nonZero((project as any)?.team_member_id) ||
        nonZero((project as any)?.team_member_membership_id) ||
        nonZero((project as any)?.team_member_global_user_id) ||
        String(project?.team_member_name || "").trim()
    );

    // license_type wins. Both agent + TM present and end user is agent → Agent Information.
    const useAgent =
      isAgentLicense ||
      (!isTeamMemberLicense && hasAgentDetails) ||
      (!isTeamMemberLicense && !hasTeamMemberDetails);

    const customerFullName = [project?.first_name, project?.last_name]
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();

    if (useAgent) {
      return {
        cardTitle: "Agent Information",
        nameLabel: "Agent Name",
        nameValue:
          String(project?.agent_name || "").trim() || customerFullName || "—",
        refreshAriaLabel: "Refresh agent information",
        refreshErrorLabel: "Failed to refresh agent information.",
      };
    }

    return {
      cardTitle: "Team Member Information",
      nameLabel: "Team Member Name",
      nameValue:
        String(project?.team_member_name || "").trim() || customerFullName || "—",
      refreshAriaLabel: "Refresh team member information",
      refreshErrorLabel: "Failed to refresh team member information.",
    };
  }, [project]);

  const agentOrTeamNameLabel = endUserPartyInfo.nameLabel;
  const agentOrTeamNameValue = endUserPartyInfo.nameValue;
  const partyOfficeName = String(
    project?.office?.officeName ||
      project?.office?.name ||
      (project as any)?.office_name ||
      ""
  ).trim();
  const showPartyOfficeName =
    endUserPartyInfo.cardTitle === "Team Member Information" ||
    String(project?.license_type || "")
      .trim()
      .toUpperCase()
      .replace(/-/g, "_")
      .includes("TEAM_MEMBER") ||
    String(project?.license_type || "").trim().toUpperCase() === "TEAMMEMBER";

  const displayAddress = useMemo(() => displayAddressFromProject(project), [project]);

  const sharedFileCount = useMemo(() => {
    const keys = new Set<string>();
    for (const r of proofResults) {
      const fid = String(r.file_id ?? "").trim();
      if (fid) keys.add(`fid:${fid}`);
      else {
        const pid = String(r.id ?? "").trim();
        if (pid) keys.add(`proof:${pid}`);
      }
    }
    for (const f of sharedFiles) {
      const fid = String(f.file_id ?? "").trim();
      if (fid) keys.add(`fid:${fid}`);
      else {
        const sid = String(f._id ?? "").trim();
        if (sid) keys.add(`sh:${sid}`);
      }
    }
    return keys.size;
  }, [proofResults, sharedFiles]);
  const otherSharedFiles = useMemo(
    () => sharedFiles.filter((f) => !f.is_proof),
    [sharedFiles]
  );

  const hasComposerAttachments = attachedDraftFiles.length > 0;
  const canSendMessage =
    (messageDraft.trim().length > 0 || hasComposerAttachments) &&
    (!hasComposerAttachments || designProofChoice !== null) &&
    !isSendingMessage;

  useEffect(() => {
    if (!hasComposerAttachments) setDesignProofChoice(null);
  }, [hasComposerAttachments]);

  useEffect(() => {
    if (!filePreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFilePreview(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filePreview]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleDocClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedPicker = emojiPickerRef.current?.contains(target);
      const clickedButton = emojiButtonRef.current?.contains(target);
      if (!clickedPicker && !clickedButton) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [showEmojiPicker]);

  useEffect(() => {
    if (!showActions) return;
    const handleDocClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!actionsMenuWrapRef.current?.contains(target)) {
        setShowActions(false);
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowActions(false);
    };
    document.addEventListener("mousedown", handleDocClick);
    window.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleDocClick);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [showActions]);

  useEffect(() => {
    if (!messages.length) return;
    if (!shouldScrollToBottomRef.current) return;
    const listEl = convoListRef.current;
    if (!listEl) return;
    requestAnimationFrame(() => {
      listEl.scrollTop = listEl.scrollHeight;
      shouldScrollToBottomRef.current = false;
    });
  }, [messages]);

  useEffect(() => {
    const pending = adjustPrependScrollRef.current;
    if (!pending) return;
    const listEl = convoListRef.current;
    if (!listEl) return;
    requestAnimationFrame(() => {
      const delta = listEl.scrollHeight - pending.previousHeight;
      listEl.scrollTop = pending.previousTop + Math.max(0, delta);
      adjustPrependScrollRef.current = null;
    });
  }, [messages]);

  useEffect(() => {
    if (tab !== "conversation") return;
    if (isLoadingOlderMessages) return;
    if (messagesPage <= 1) return;
    const listEl = convoListRef.current;
    if (!listEl) return;
    // When message count is too small (e.g. limit=2), no scrollbar appears,
    // so the user cannot trigger "scroll up to load older". Auto-fetch older pages.
    if (listEl.scrollHeight <= listEl.clientHeight + 1) {
      void loadOlderMessages();
    }
  }, [tab, messages, messagesPage, isLoadingOlderMessages, loadOlderMessages]);

  const removeDraftAttachment = (id: string) => {
    setAttachedDraftFiles((prev) => prev.filter((x) => x.id !== id));
  };

  const appendDraftFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next: { id: string; file: File }[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const file = list.item(i);
      if (file) {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          file,
        });
      }
    }
    setAttachedDraftFiles((prev) => [...prev, ...next]);
    setDesignProofChoice(null);
  };

  const truncateFileName = (name: string, max = 28) => {
    if (name.length <= max) return name;
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
    const base = ext ? name.slice(0, name.length - ext.length) : name;
    const keep = max - ext.length - 3;
    return `${base.slice(0, Math.max(6, keep))}…${ext}`;
  };

  const handleSendMessage = async () => {
    if (!projectId || !token || !project) return;
    const text = messageDraft.trim();
    const hasFiles = attachedDraftFiles.length > 0;
    if (!text && !hasFiles) return;
    if (hasFiles && designProofChoice === null) {
      showError("Select Yes or No for Design Proof.");
      return;
    }

    const signageId = resolveProjectVendorOrganizationId(project);
    if (hasFiles && !signageId) {
      showError("Missing signage id for file upload.");
      return;
    }

    setIsSendingMessage(true);
    const filesSnapshot = attachedDraftFiles.map((x) => x.file);
    try {
      let replyFileIds: string[] = [];

      if (hasFiles && signageId) {
        const files = filesSnapshot;
        let uploadRes: unknown;
        if (designProofChoice === "yes") {
          const selectedVersion = Number(selectedProofKey.replace("v-", ""));
          const nextVersion =
            Number.isFinite(selectedVersion) && selectedVersion > 0
              ? selectedVersion + 1
              : proofVersionGroups.length + 1;
          uploadRes = await uploadSignageProjectFiles(token, signageId, projectId, files, {
            is_proof: true,
            version: nextVersion,
          });
        } else {
          uploadRes = await uploadSignageProjectFiles(token, signageId, projectId, files);
        }
        replyFileIds = extractUploadedFileIds(uploadRes);
        if (replyFileIds.length === 0) {
          throw new Error("Upload did not return file ids.");
        }
      }

      // Text-only, file-only with default body, or both.
      const hasLinkedFiles = replyFileIds.length > 0;
      const messageForApi =
        text.length > 0 ? text : hasLinkedFiles ? "File Uploaded" : "";

      if (messageForApi.trim().length > 0 || hasLinkedFiles) {
        const replyToTrim = replyToMessageId.trim();
        await postProjectMessage(token, projectId, messageForApi, {
          ...(replyToTrim ? { reply_to_message_id: replyToTrim } : {}),
          ...(hasLinkedFiles ? { reply_to_file_ids: replyFileIds } : {}),
        });
      }

      await reloadProjectData();
      setMessageDraft("");
      setReplyToMessageId("");
      setAttachedDraftFiles([]);
      setDesignProofChoice(null);
      showSuccess("Message sent successfully.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to send message.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleToggleClientMessageMute = async () => {
    if (!projectId || !token || !project) return;
    const nextMuted = !conversationEndUserEmailMuted;
    const nextSendToClient = !nextMuted;
    setConversationEndUserEmailMuted(nextMuted);
    try {
      await updateProjectSendMessageToClient(token, projectId, nextSendToClient);
      setProject((prev) =>
        prev
          ? ({
              ...prev,
              send_message_to_client: nextSendToClient,
              mute_messages_to_client: nextMuted,
            } as SignageProjectDetail)
          : prev
      );
    } catch (e) {
      setConversationEndUserEmailMuted(!nextMuted);
      showError(e instanceof Error ? e.message : "Failed to update mute setting.");
    }
  };

  const handleInsertEmoji = (emoji: string) => {
    const input = messageInputRef.current;
    if (!input) {
      setMessageDraft((prev) => `${prev}${emoji}`);
      setShowEmojiPicker(false);
      return;
    }
    const start = input.selectionStart ?? messageDraft.length;
    const end = input.selectionEnd ?? messageDraft.length;
    const next = `${messageDraft.slice(0, start)}${emoji}${messageDraft.slice(end)}`;
    setMessageDraft(next);
    setShowEmojiPicker(false);
    window.requestAnimationFrame(() => {
      input.focus();
      const caret = start + emoji.length;
      input.setSelectionRange(caret, caret);
    });
  };

  const performApproveSelectedProof = async () => {
    if (!projectId || !token || !selectedProofKey) return;
    const version = Number(selectedProofKey.replace("v-", ""));
    if (!Number.isFinite(version) || version <= 0) return;
    const billing = publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
    const shipping = sameAsBilling
      ? billing
      : publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
    const shippingMethod =
      SHIPPING_METHODS.find((m) => m.id === selectedShippingMethodId) || SHIPPING_METHODS[2];
    setIsApprovingProof(true);
    try {
      const publicAccessToken = await resolvePublicAccessToken();
      if (!publicAccessToken) {
        throw new Error("Failed to resolve public project access token.");
      }
      const updatedBy = buildProjectUpdatedByFromAuth("Signage User");
      await approvePublicProjectProof(
        token,
        projectId,
        publicAccessToken,
        version,
        updatedBy,
        {
          versions: [version],
          file_quantities: selectedProofFilesForApproval
            .map((file) => ({
              project_file_id: file.id,
              quantity:
                Number.isFinite(Number(selectedFileQuantities[file.id])) &&
                Number(selectedFileQuantities[file.id]) >= 0
                  ? Math.floor(Number(selectedFileQuantities[file.id]))
                  : 0,
            }))
            .filter((row) => row.project_file_id),
          billing_address: toApprovalAddressPayload(billing),
          shipping_address: toApprovalAddressPayload(shipping),
          both_address_same: Boolean(sameAsBilling),
          po_number: approvalPoNumber.trim(),
          shipping_method: {
            name: shippingMethod.name,
            description: shippingMethod.description,
          },
        }
      );
      // Approve-proof updates project status / workflow on the server; reloadProjectData alone
      // does not refresh `project`, so the UI looked stale until a full page refresh.
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await reloadProjectData(detail);
      showSuccess(`Proof V${version} marked as approved.`);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to approve proof.");
    } finally {
      setIsApprovingProof(false);
    }
  };

  const handleApproveSelectedProof = async () => {
    setEditingApprovedProofVersion(null);
    setPendingApproveSelectedProof(true);
    await loadPublicAddresses();
    setSelectedBillingAddressId("");
    setSelectedShippingAddressId("");
    setSameAsBilling(false);
    setSelectedShippingMethodId("ups_shipping");
    const selectedVersion = Number(selectedProofKey.replace("v-", ""));
    const group = proofVersionGroups.find((g) => g.version === selectedVersion);
    const nextQuantities: Record<string, number> = {};
    (group?.files || []).forEach((f: any) => {
      const fid = String(f?.id || f?._id || f?.project_file_id || f?.file_id || "").trim();
      if (fid) nextQuantities[fid] = 0;
    });
    setSelectedFileQuantities(nextQuantities);
    setApprovalPoNumber(
      String((project as any)?.po_number ?? (project as any)?.poNumber ?? "").trim()
    );
    setShowAddressPickerModal(true);
  };

  const handleEditApprovedProofVersion = async (version: number) => {
    if (!Number.isFinite(version) || version <= 0) return;
    setEditingApprovedProofVersion(version);
    setPendingApproveSelectedProof(false);
    await loadPublicAddresses();
    const proofMeta = approvedProofMetaByVersion.get(version);
    const billingFromMeta = proofMeta?.billingAddress || null;
    const shippingFromMeta = proofMeta?.shippingAddress || null;
    const billingId = pickAddressId(billingFromMeta);
    const shippingId = pickAddressId(shippingFromMeta);
    setSelectedBillingAddressId(billingId);
    setSelectedShippingAddressId(shippingId);
    setSameAsBilling(
      typeof proofMeta?.bothAddressSame === "boolean"
        ? Boolean(proofMeta?.bothAddressSame)
        : Boolean(billingId && shippingId && billingId === shippingId)
    );
    const shippingMethodName = String(proofMeta?.shippingMethod?.name || "").trim().toLowerCase();
    const shippingMethodMatch = SHIPPING_METHODS.find(
      (m) => String(m.name || "").trim().toLowerCase() === shippingMethodName
    );
    setSelectedShippingMethodId(shippingMethodMatch?.id || "ups_shipping");

    const group = proofVersionGroups.find((g) => g.version === version);
    const metaQtyMap = new Map<string, number>();
    (proofMeta?.fileQuantities || []).forEach((row) => {
      const id = String(row?.project_file_id || "").trim();
      if (!id) return;
      const qty = Number(row?.quantity);
      metaQtyMap.set(id, Number.isFinite(qty) && qty >= 0 ? Math.floor(qty) : 0);
    });
    const nextQuantities: Record<string, number> = {};
    (group?.files || []).forEach((f: any) => {
      const fid = String(f?.id || f?._id || f?.project_file_id || f?.file_id || "").trim();
      if (!fid) return;
      const rowQty = Number(f?.quantity);
      nextQuantities[fid] = metaQtyMap.has(fid)
        ? Number(metaQtyMap.get(fid))
        : Number.isFinite(rowQty) && rowQty >= 0
          ? Math.floor(rowQty)
          : 0;
    });
    setSelectedFileQuantities(nextQuantities);
    setApprovalPoNumber(
      String(
        proofMeta?.poNumber ??
          (project as any)?.po_number ??
          (project as any)?.poNumber ??
          ""
      ).trim()
    );
    setShowAddressPickerModal(true);
  };

  const handleSaveApprovedProofDetails = async (version: number) => {
    if (!projectId || !token) return;
    const billing = publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
    const shipping = sameAsBilling
      ? billing
      : publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
    if (!billing || !shipping) {
      showError("Please select both billing and shipping addresses.");
      return;
    }
    const shippingMethod =
      SHIPPING_METHODS.find((m) => m.id === selectedShippingMethodId) || SHIPPING_METHODS[2];
    setIsApprovingProof(true);
    try {
      const publicAccessToken = await resolvePublicAccessToken();
      if (!publicAccessToken) {
        throw new Error("Failed to resolve public project access token.");
      }
      const updatedBy = buildProjectUpdatedByFromAuth("Signage User");
      const fileQuantitiesPayload = selectedProofFilesForApproval
        .map((file) => ({
          project_file_id: file.id,
          quantity:
            Number.isFinite(Number(selectedFileQuantities[file.id])) &&
            Number(selectedFileQuantities[file.id]) >= 0
              ? Math.floor(Number(selectedFileQuantities[file.id]))
              : 0,
        }))
        .filter((row) => row.project_file_id);
      await updatePublicApprovedProofDetails(token, projectId, publicAccessToken, {
        file_quantities: fileQuantitiesPayload,
        updated_by: updatedBy,
        billing_address: toApprovalAddressPayload(billing),
        shipping_address: toApprovalAddressPayload(shipping),
        both_address_same: Boolean(sameAsBilling),
        po_number: approvalPoNumber.trim(),
        shipping_method: {
          name: shippingMethod.name,
          description: shippingMethod.description,
        },
      });
      setShowAddressPickerModal(false);
      setEditingApprovedProofVersion(null);
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await reloadProjectData(detail);
      showSuccess(`Proof V${version} approved details updated.`);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to update approved proof details.");
    } finally {
      setIsApprovingProof(false);
    }
  };

  const handleSaveApprovedProofOrderSummary = async (version: number, orderSummary: string) => {
    if (!projectId || !token) return;
    const v = Math.floor(Number(version));
    if (!Number.isFinite(v) || v <= 0) return;
    setOrderSummarySavingVersion(v);
    try {
      const publicAccessToken = await resolvePublicAccessToken();
      if (!publicAccessToken) {
        throw new Error("Failed to resolve public project access token.");
      }
      await updatePublicApprovedProofOrderSummary(token, projectId, publicAccessToken, v, orderSummary);
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await reloadProjectData(detail);
      showSuccess(`Summary saved for Proof V${v}.`);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to save summary note.");
      throw e;
    } finally {
      setOrderSummarySavingVersion(null);
    }
  };

  const handleUnapproveProofVersion = async (version: number) => {
    if (!projectId || !token) return;
    if (!Number.isFinite(version) || version <= 0) return;
    setUnapprovingProofVersion(version);
    try {
      await unapproveProjectProofVersions(token, projectId, [version]);
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await reloadProjectData(detail);
      showSuccess(`Proof V${version} unapproved.`);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to unapprove proof.");
    } finally {
      setUnapprovingProofVersion(null);
    }
  };

  const handleSubmitApprovalAddress = async (values: AddressFormValues) => {
    if (!projectId || !token || !project) {
      showError("Project context is missing.");
      return;
    }
    const addressOwnerId = resolveProjectAddressOwnerId(
      project as Record<string, unknown>
    );
    if (!addressOwnerId) {
      showError("Address owner id is missing for this project.");
      return;
    }
    const officeId = String(
      (project as { office?: { _id?: string; id?: string }; office_id?: string } | null)?.office?._id ||
        (project as { office?: { _id?: string; id?: string } } | null)?.office?.id ||
        (project as { office_id?: string } | null)?.office_id ||
        ""
    ).trim();
    const isOfficeOwner = Boolean(officeId && addressOwnerId === officeId);
    setAddressSubmitting(true);
    try {
      const toCoordinate = (value: unknown) => {
        const coordinate = Number(value ?? 0);
        return Number.isFinite(coordinate) ? coordinate : 0;
      };
      const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
      const addressCountryCode = String(values.countryCode || "").trim() || US_COUNTRY_DEFAULTS.countryCode;
      const addressLatitude = toCoordinate(values.latitude);
      const addressLongitude = toCoordinate(values.longitude);
      const publicAccessRes = await fetchProjectPublicAccess(token, projectId);
      const p = (publicAccessRes as any)?.data ?? (publicAccessRes as any) ?? {};
      const rawPublicUrl = String(p?.public_url || "").trim();
      const tokenFromPublicUrl = rawPublicUrl.includes("?")
        ? new URLSearchParams(rawPublicUrl.split("?")[1]).get("token") || ""
        : "";
      const publicAccessToken = String(
        p?.access_token || p?.accessToken || tokenFromPublicUrl || ""
      ).trim();
      if (!publicAccessToken) {
        throw new Error("Failed to resolve public project access token.");
      }

      await createPublicAddressForProjectUser(token, addressOwnerId, publicAccessToken, {
        customerId: addressOwnerId,
        globalUserId: addressOwnerId,
        organizationId: resolveProjectVendorOrganizationId(project),
        officeId: isOfficeOwner ? officeId : undefined,
        userType: isOfficeOwner ? "OFFICE" : "GLOBAL_USER",
        label: String(values.label || "").trim(),
        firstName: String(values.firstName || "").trim(),
        lastName: String(values.lastName || "").trim(),
        company: "",
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        state: values.state,
        zip: values.zipCode,
        country: addressCountry,
        phone: String(values.phone || "").trim(),
        cityCode: String(values.cityCode || "").trim(),
        stateCode: (() => {
          const fromForm = String(values.stateCode || "").trim();
          if (fromForm) return fromForm;
          const stateOnly = String(values.state || "").trim();
          return /^[A-Za-z]{2}$/.test(stateOnly) ? stateOnly.toUpperCase() : "";
        })(),
        countryCode: addressCountryCode,
        latitude: addressLatitude,
        longitude: addressLongitude,
      });
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await loadPublicAddresses();
      setShowAddressModal(false);
      showSuccess("Address added successfully.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to create addresses");
    } finally {
      setAddressSubmitting(false);
    }
  };

  const handleConfirmAddressAndApprove = async () => {
    if (!pendingApproveSelectedProof && !editingApprovedProofVersion) return;
    const billing = publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
    const shipping = sameAsBilling
      ? billing
      : publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
    if (!billing || !shipping) {
      showError("Please select both billing and shipping addresses.");
      return;
    }
    if (editingApprovedProofVersion) {
      await handleSaveApprovedProofDetails(editingApprovedProofVersion);
      return;
    }
    setShowAddressPickerModal(false);
    setPendingApproveSelectedProof(false);
    await performApproveSelectedProof();
  };

  const handleSelectOrderType = async () => {
    if (!projectId || !token || !project || !selectedOrderType) return;
    const linked_order_type = selectedOrderType === "sales" ? "sales_order" : "service_order";
    setIsSavingOrderType(true);
    try {
      await updateProjectLinkedOrderType(token, projectId, {
        title: project.title || "",
        notes: project.notes || "",
        linked_order_type,
        ...(project.license_type ? { license_type: project.license_type } : {}),
        ...(quoteAssigneeMembershipId(selectedQuoteAssigneeOption)
          ? {
            assigned_to_membership_id: quoteAssigneeMembershipId(selectedQuoteAssigneeOption),
            ...(asMongoObjectId(selectedQuoteAssigneeOption?.globalUserId)
              ? { assigned_to_global_user_id: asMongoObjectId(selectedQuoteAssigneeOption?.globalUserId) }
              : {}),
            assigned_to_name: selectedQuoteAssigneeOption?.label || "",
            assigned_to_email: String(selectedQuoteAssigneeOption?.email || "").trim(),
          }
          : {}),
      });
      showSuccess("Order type selected.");
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      const nextRoute =
        selectedOrderType === "sales"
          ? `${baseRoute}/createOrder?project_id=${encodeURIComponent(projectId)}`
          : `${baseRoute}/jobs/addInstallation?project_id=${encodeURIComponent(projectId)}`;
      navigate(nextRoute);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to set order type.");
    } finally {
      setIsSavingOrderType(false);
    }
  };

  const handleSaveQuoteAssignee = async () => {
    if (!projectId || !token || !project) return;
    const assignedMembershipId = quoteAssigneeMembershipId(selectedQuoteAssigneeOption);
    if (!assignedMembershipId) {
      showError("Please select a team member.");
      return;
    }
    const assignedGlobalUserId = asMongoObjectId(selectedQuoteAssigneeOption?.globalUserId);
    setIsSavingQuoteAssignee(true);
    try {
      await updateProjectLinkedOrderType(token, projectId, {
        title: project.title || "",
        notes: project.notes || "",
        ...(project.linked_order_type ? { linked_order_type: project.linked_order_type } : {}),
        ...(project.license_type ? { license_type: project.license_type } : {}),
        assigned_to_membership_id: assignedMembershipId,
        ...(assignedGlobalUserId ? { assigned_to_global_user_id: assignedGlobalUserId } : {}),
        assigned_to_name: selectedQuoteAssigneeOption?.label || "",
        assigned_to_email: String(selectedQuoteAssigneeOption?.email || "").trim(),
      });
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      showSuccess("Quote assignee updated.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to update quote assignee.");
    } finally {
      setIsSavingQuoteAssignee(false);
    }
  };

  const handleRefreshAgentInfo = async () => {
    if (!projectId || !token) return;
    try {
      const documentId = String((project as any)?._id || project?.id || projectId || "").trim();
      const membershipId = pickSyncMembershipId(
        (project as any)?.customer_membership_id,
        (project as any)?.agent_membership_id,
        (project as any)?.team_member_membership_id
      );
      if (documentId && membershipId) {
        try {
          await syncUserEmailToDocument(token, {
            membership_id: membershipId,
            id: documentId,
            collection_name: "project",
          });
        } catch (syncErr) {
          safeConsole.warn("sync-user-email-to-document failed:", syncErr);
        }
      }
      const detail = await fetchProjectById(token, projectId);
      setProject(detail);
      await reloadProjectData(detail);
    } catch (e) {
      showError(
        e instanceof Error ? e.message : endUserPartyInfo.refreshErrorLabel
      );
    }
  };

  const handleReopenProject = async () => {
    if (!projectId || !token || isReopeningProject) return;
    setIsReopeningProject(true);
    try {
      const reopened = await reopenProject(token, projectId);
      setProject(reopened);
      await reloadProjectData(reopened);
      showSuccess("Project reopened successfully.");
      setShowReopenProjectModal(false);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to reopen project.");
    } finally {
      setIsReopeningProject(false);
    }
  };

  const normalizedStatus = normalizeProjectStatus(project?.status);
  const statusPillClass = getStatusPillClass(normalizedStatus);
  const linkedQuoteLabel = useMemo(() => {
    const p = project as any;
    return (
      p?.linked_quote_number ||
      p?.linked_quote ||
      p?.linked_order_number ||
      p?.linked_order_id ||
      p?.quote_number ||
      ""
    );
  }, [project]);
  const linkedOrderViewPath = useMemo(() => {
    const p = project as any;
    return getLinkedOrderViewPath(baseRoute, {
      linked_order_id: p?.linked_order_id || p?.linked_order?.id,
      linked_order_type: p?.linked_order_type,
    });
  }, [project, baseRoute]);
  const workflowStages = useMemo(
    () =>
      buildWorkflowStages(
        normalizedStatus,
        linkedQuoteLabel,
        selectedQuoteAssigneeOption?.label || ""
      ),
    [normalizedStatus, linkedQuoteLabel, selectedQuoteAssigneeOption?.label]
  );
  const canShowProofSelector = normalizedStatus === "IN_PROGRESS";
  const canShowAssignForQuote = normalizedStatus === "DESIGN_APPROVED" || normalizedStatus === "IN_PROGRESS";
  const canShowCreateQuote =
    normalizedStatus === "IN_PROGRESS" || normalizedStatus === "DESIGN_APPROVED";
  const canShowQuickActions = canShowProofSelector || canShowCreateQuote || canShowAssignForQuote;
  const projectPhone = project?.customer_phone || "—";
  const selectedProofVersionNumber = Number(selectedProofKey.replace("v-", ""));
  const activeApprovalVersionNumber =
    Number.isFinite(Number(editingApprovedProofVersion)) && Number(editingApprovedProofVersion) > 0
      ? Number(editingApprovedProofVersion)
      : selectedProofVersionNumber;
  const selectedProofFilesForApproval = useMemo(() => {
    const match = proofVersionGroups.find((g) => g.version === activeApprovalVersionNumber);
    if (!match) return [] as Array<{ id: string; name: string }>;
    return match.files
      .map((f: any) => ({
        id: String(f?.id || f?._id || f?.project_file_id || f?.file_id || "").trim(),
        name: String(f?.original_name || f?.file_name || "File"),
        quantity: Number.isFinite(Number(f?.quantity)) ? Math.max(0, Math.floor(Number(f?.quantity))) : 0,
      }))
      .filter((f) => f.id);
  }, [proofVersionGroups, activeApprovalVersionNumber]);
  const approvedVersionNumbers = useMemo(
    () => {
      const versions = new Set<number>();
      const projectAny = project as any;
      const approvedVersionList = Array.isArray(projectAny?.approved_proof_versions)
        ? projectAny.approved_proof_versions
        : [];
      approvedVersionList.forEach((v: unknown) => {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) versions.add(n);
      });
      const approvedProofRows = Array.isArray(projectAny?.approved_proofs) ? projectAny.approved_proofs : [];
      approvedProofRows.forEach((row: any) => {
        const n = Number(row?.version);
        if (Number.isFinite(n) && n > 0) versions.add(n);
      });
      proofResults
        .filter((item: any) => isApprovedFlag(item?.is_approved))
        .forEach((item) => {
          const n = Number(item.version);
          if (Number.isFinite(n) && n > 0) versions.add(n);
        });
      return versions;
    },
    [project, proofResults]
  );
  const approvedProofMetaByVersion = useMemo(
    () => buildApprovedProofMetaByVersion(project, proofResults),
    [project, proofResults]
  );
  const firstApprovedMessageIdByVersion = useMemo(() => {
    const map = new Map<number, string>();
    messages.forEach((m) => {
      const version = Number(m.proofVersion ?? 0);
      if (!Number.isFinite(version) || version <= 0) return;
      const hasProof = m.replyFiles.some((f) => f.isProof);
      if (!hasProof) return;
      if (!approvedVersionNumbers.has(version)) return;
      if (!map.has(version)) {
        map.set(version, m.id);
      }
    });
    return map;
  }, [messages, approvedVersionNumbers]);
  const selectedProofAlreadyApproved =
    Number.isFinite(selectedProofVersionNumber) && approvedVersionNumbers.has(selectedProofVersionNumber);

  const renderAttachment = (file: string) => (
    <div className="pq-attachment" key={file}>
      <div className="pq-attachment-left">
        <div className="pq-attachment-fileicon">
          <i className="ri-file-4-line" />
        </div>
        <div className="pq-attachment-content">
          <div className="pq-attachment-topline">
            <span className="pq-attachment-name">{file}</span>
          </div>
        </div>
      </div>
      <DownloadIconButton type="button" className="pq-attachment-download" title="Download" size={34} />
    </div>
  );

  const triggerFileDownload = useCallback(
    (href: string, fileName: string, fileId?: string) => {
      let id = String(fileId ?? "").trim();
      if (!id && href) {
        const m = String(href).match(/\/public\/work-drive\/files\/([^/?#]+)\/download/i);
        id = m?.[1] ? decodeURIComponent(m[1]) : "";
      }
      const apiDownloadUrl = id ? publicWorkDriveFileDownloadUrl(id) : "";
      const effectiveHref = apiDownloadUrl || href;
      if (!effectiveHref) {
        showError("Download link unavailable.");
        return;
      }
      const link = document.createElement("a");
      link.href = effectiveHref;
      link.download = fileName || "";
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    []
  );

  const handleHardDeleteFile = useCallback(
    async (fileId?: string) => {
      const id = String(fileId ?? "").trim();
      if (!id) {
        showError("File id missing.");
        return;
      }
      if (!token) {
        showError("Please sign in again.");
        return;
      }
      try {
        await hardDeleteWorkDriveFile(token, id);
        await reloadProjectData(project);
        showSuccess("File deleted.");
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to delete file.");
      }
    },
    [token, project]
  );

  const handleDeleteProjectFile = useCallback(
    async (projectFileId?: string) => {
      const fid = String(projectFileId ?? "").trim();
      if (!fid) {
        showError("File id missing.");
        return;
      }
      if (!token || !projectId) {
        showError("Please sign in again.");
        return;
      }
      try {
        await deleteProjectFileByProject(token, projectId, fid);
        await reloadProjectData(project);
        await fetchProjectById(token,projectId);
        showSuccess("File deleted.");
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to delete file.");
      }
    },
    [token, projectId, project]
  );

  const openDeleteModal = useCallback(
    (targetType: "project" | "hard", fileId?: string, fileName?: string) => {
      const id = String(fileId ?? "").trim();
      if (!id) {
        showError("File id missing.");
        return;
      }
      setDeleteTargetType(targetType);
      setDeleteTargetId(id);
      setDeleteTargetName(String(fileName || "file").trim() || "file");
      setOpenDelete(true);
    },
    []
  );

  const closeDeleteModal = useCallback(() => {
    if (isDeletingFile) return;
    setOpenDelete(false);
    setDeleteTargetId(null);
    setDeleteTargetName("");
    setDeleteTargetType("project");
  }, [isDeletingFile]);

  const confirmDeleteFile = useCallback(async () => {
    const id = String(deleteTargetId || "").trim();
    if (!id) {
      showError("File id missing.");
      return;
    }
    setIsDeletingFile(true);
    try {
      if (deleteTargetType === "hard") {
        await handleHardDeleteFile(id);
      } else {
        await handleDeleteProjectFile(id);
      }
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
      setDeleteTargetType("project");
    } finally {
      setIsDeletingFile(false);
    }
  }, [deleteTargetId, deleteTargetType, handleHardDeleteFile, handleDeleteProjectFile]);

  const renderReplyFile = (file: UiReplyFile, messageTimeLabel?: string) => {
    const href = resolveProjectFileDownloadUrl({
      file_id: file.apiFileId,
      file_url: file.previewUrl || undefined,
    });
    const previewRaw = (file.previewUrl || "").trim();
    const canImgPreview =
      isLikelyImagePreview(file.fileType, file.name) && isHttpUrl(previewRaw);
    const effectiveHref = href || (isHttpUrl(previewRaw) ? previewRaw : "");
    const projectFileId = String(file.id || "").trim();

    const openPreview = () => {
      if (!effectiveHref && !previewRaw) return;
      setFilePreview({
        name: file.name,
        previewUrl: canImgPreview ? previewRaw : "",
        href: effectiveHref || previewRaw,
        apiFileId: file.apiFileId,
        sizeLabel: formatFileSizeForUi(file.fileSize),
        dateLabel: messageTimeLabel || "—",
      });
    };

    const typeLabel = fileTypeLabel(file.fileType, file.name);
    return (
      <div className="pq-attachment pq-attachment--message-file" key={file.id}>
        <div className="pq-attachment-left">
          <div className={`pq-file-type ${typeLabel.toLowerCase()}`}>{typeLabel}</div>
          <div className="pq-attachment-content">
            <div className="pq-attachment-topline">
              <span className="pq-attachment-name">{file.name}</span>
              {file.proofLabel ? (
                <span className="pq-attachment-proof-chip">{file.proofLabel}</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="pq-attachment-actions">
          {(canImgPreview || Boolean(effectiveHref)) && (
            <ViewIconButton
              type="button"
              className="pq-attachment-download pq-attachment-view"
              title="View"
              onClick={openPreview}
              size={34}
            />
          )}
          {href ? (
            <DownloadIconButton
              type="button"
              className="pq-attachment-download"
              title="Download"
              onClick={() => triggerFileDownload(href, file.name, file.apiFileId)}
              size={34}
            />
          ) : (
            <DownloadIconButton
              type="button"
              disabled
              className="pq-attachment-download pq-attachment-download--disabled"
              title="Download"
              size={34}
            />
          )}
          {projectFileId ? (
            <DeleteIconButton
              type="button"
              className="pq-attachment-download pq-shared-file-delete"
              title="Delete file"
              onClick={() => openDeleteModal("project", projectFileId, file.name)}
              size={34}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderProofConversationRow = (file: UiReplyFile, index: number, message: UiMessage) => {
    const href = resolveProjectFileDownloadUrl({
      file_id: file.apiFileId,
      file_url: file.previewUrl || undefined,
    });
    const letter = String.fromCharCode(65 + index);
    const previewRaw = (file.previewUrl || "").trim();
    const canImgPreview =
      isLikelyImagePreview(file.fileType, file.name) && isHttpUrl(previewRaw);
    const effectiveHref = href || (isHttpUrl(previewRaw) ? previewRaw : "");
    const qty = Number.isFinite(Number(file.quantity)) ? Math.max(0, Math.floor(Number(file.quantity))) : 0;
    const projectFileId = String(file.id || "").trim();

    const openPreview = () => {
      if (!effectiveHref && !previewRaw) return;
      setFilePreview({
        name: file.name,
        previewUrl: canImgPreview ? previewRaw : "",
        href: effectiveHref || previewRaw,
        apiFileId: file.apiFileId,
        sizeLabel: formatFileSizeForUi(file.fileSize),
        dateLabel: message.time,
      });
    };

    return (
      <div className="pq-msg-proof-row pq-msg-proof-row--rich" key={file.id}>
        <div className="pq-msg-proof-row-inner">
          <div className="pq-msg-proof-row-top">
            <div className="pq-msg-proof-row-main">
              <i className="ri-file-4-line pq-msg-proof-row-icon" aria-hidden />
              <span className="pq-msg-proof-row-letter">File {letter}</span>
              <span className="pq-msg-proof-row-name" title={file.name}>
                {file.name}
              </span>
              {/* {file.designApprovedByName ? (
                <span className="pq-msg-proof-file-approved-pill">
                  <i className="ri-check-line" aria-hidden />
                  Approved
                </span>
              ) : null} */}
            </div>
            <div className="pq-msg-proof-row-actions">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#dcfce7",
                  color: "#166534",
                  borderRadius: 999,
                  padding: "6px 10px",
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#16a34a",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                  }}
                >
                  {qty}
                </span>
                {qty === 1 ? "unit" : "units"}
              </span>
              {(canImgPreview || effectiveHref) && (
                <ViewIconButton
                  type="button"
                  className="pq-msg-proof-row-view"
                  title="View"
                  onClick={openPreview}
                  size={36}
                />
              )}
              {effectiveHref ? (
                <DownloadIconButton
                  type="button"
                  className="pq-msg-proof-row-download"
                  title="Download"
                  onClick={() => triggerFileDownload(effectiveHref, file.name, file.apiFileId)}
                  size={36}
                />
              ) : (
                <DownloadIconButton
                  type="button"
                  disabled
                  className="pq-msg-proof-row-download pq-msg-proof-row-download--disabled"
                  title="Download"
                  size={36}
                />
              )}
              <DeleteIconButton
                type="button"
                className="pq-msg-proof-row-download pq-shared-file-delete"
                title="Delete file"
                onClick={() => openDeleteModal("project", projectFileId, file.name)}
                size={36}
              />
            </div>
          </div>
          <div className="pq-msg-proof-row-sub">
            <i className="ri-user-smile-line" aria-hidden />
            <span>{message.sender}</span>
            <span className="pq-msg-proof-row-sub-dot">·</span>
            <span>{message.time}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSharedProofFileRow = (
    f: ProjectProofSelect2Row,
    version: number,
    index: number
  ) => {
    const name = f.original_name || f.file_name || "—";
    const workDriveId = String(f.file_id ?? "").trim();
    const href = resolveProjectFileDownloadUrl({
      file_id: workDriveId,
      file_url: f.file_url,
    });
    const previewUrl = String(f.preview_url || "").trim();
    const canImgPreview =
      isLikelyImagePreview(f.file_type, name) && isHttpUrl(previewUrl);
    const effectiveHref = href || (isHttpUrl(previewUrl) ? previewUrl : "");
    const letter = String.fromCharCode(65 + index);
    const qty = Number.isFinite(Number((f as any).quantity))
      ? Math.max(0, Math.floor(Number((f as any).quantity)))
      : 0;
    const byInfo = resolveSharedFileUploaderDisplay((f as any).created_by, project);
    const by = byInfo.name;
    const byRole = byInfo.role;
    const createdAt = (f as any).created_at as string | undefined;

    const openSharedPreview = () => {
      if (!effectiveHref && !previewUrl) return;
      setFilePreview({
        name,
        previewUrl: canImgPreview ? previewUrl : "",
        href: effectiveHref || previewUrl,
        apiFileId: workDriveId || undefined,
        sizeLabel: formatFileSizeForUi(f.file_size),
        dateLabel: "—",
      });
    };

    return (
      <div className="pq-msg-proof-row pq-msg-proof-row--rich" key={f.id}>
        <div className="pq-msg-proof-row-inner">
          <div className="pq-msg-proof-row-top">
            <div className="pq-msg-proof-row-main pq-msg-proof-row-main--shared">
              <div className={`pq-file-type ${fileTypeLabel(f.file_type, name).toLowerCase()}`}>
                {fileTypeLabel(f.file_type, name)}
              </div>
              <span className="pq-msg-proof-row-letter">File {letter}</span>
              <span className="pq-msg-proof-row-name" title={name}>
                {name}
              </span>
              {/* {isApprovedFlag(f.is_approved) ? (
                <span className="pq-msg-proof-file-approved-pill">
                  <i className="ri-check-line" aria-hidden />
                  Approved
                </span>
              ) : null} */}
            </div>
            <div className="pq-msg-proof-row-actions">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#dcfce7",
                  color: "#166534",
                  borderRadius: 999,
                  padding: "6px 10px",
                  fontSize: 13,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "#16a34a",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                  }}
                >
                  {qty}
                </span>
                {qty === 1 ? "unit" : "units"}
              </span>
              {(canImgPreview || effectiveHref) && (
                <ViewIconButton
                  type="button"
                  className="pq-msg-proof-row-view"
                  title="View"
                  onClick={openSharedPreview}
                  size={36}
                />
              )}
              {effectiveHref ? (
                <DownloadIconButton
                  type="button"
                  className="pq-msg-proof-row-download"
                  title="Download"
                  onClick={() => triggerFileDownload(effectiveHref, name, workDriveId)}
                  size={36}
                />
              ) : (
                <DownloadIconButton
                  type="button"
                  disabled
                  className="pq-msg-proof-row-download pq-msg-proof-row-download--disabled"
                  title="Download"
                  size={36}
                />
              )}
              <DeleteIconButton
                type="button"
                className="pq-msg-proof-row-download pq-shared-file-delete"
                title="Delete file"
                onClick={() =>
                  openDeleteModal(
                    "project",
                    String(f.id || "").trim(),
                    name
                  )
                }
                size={36}
              />
            </div>
          </div>
          <div className="pq-msg-proof-row-sub">
            <i className="ri-time-line pq-msg-proof-row-sub-ico" aria-hidden />
            <span>{by}</span>
            {byRole ? (
              <>
                <span className="pq-msg-proof-row-sub-dot">·</span>
                <span>{byRole}</span>
              </>
            ) : null}
            <span className="pq-msg-proof-row-sub-dot">·</span>
            <span>{formatDateTime(createdAt)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSharedProofFileRowPending = (f: ProjectProofSelect2Row) => {
    const name = f.original_name || f.file_name || "—";
    const workDriveId = String(f.file_id ?? "").trim();
    const href = resolveProjectFileDownloadUrl({
      file_id: workDriveId,
      file_url: f.file_url,
    });
    const previewUrl = String(f.preview_url || "").trim();
    const canImgPreview =
      isLikelyImagePreview(f.file_type, name) && isHttpUrl(previewUrl);
    const effectiveHref = href || (isHttpUrl(previewUrl) ? previewUrl : "");
    const byInfo = resolveSharedFileUploaderDisplay((f as any).created_by, project);
    const by = byInfo.name;
    const byRole = byInfo.role;
    const createdAt = (f as any).created_at as string | undefined;

    const openSharedPreview = () => {
      if (!effectiveHref && !previewUrl) return;
      setFilePreview({
        name,
        previewUrl: canImgPreview ? previewUrl : "",
        href: effectiveHref || previewUrl,
        apiFileId: workDriveId || undefined,
        sizeLabel: formatFileSizeForUi(f.file_size),
        dateLabel: formatDateTime(createdAt),
      });
    };

    return (
      <div className="pq-file-row" key={f.id}>
        <div className={`pq-file-type ${fileTypeLabel(f.file_type, name).toLowerCase()}`}>
          {fileTypeLabel(f.file_type, name)}
        </div>

        <div className="pq-file-meta">
          <div className="pq-file-name">{name}</div>
          <div className="pq-file-sub">
            <span className="pq-file-sub-name">{by}</span>
            {byRole ? <span className="pq-file-sub-role">{byRole}</span> : null}
            <span className="pq-file-sub-time">{formatDateTime(createdAt)}</span>
          </div>
        </div>

        <div className="pq-file-actions">
          {(canImgPreview || effectiveHref) && (
            <ViewIconButton
              type="button"
              className="pq-file-action-btn"
              title="View"
              onClick={openSharedPreview}
              size={28}
            />
          )}
          {href ? (
            <DownloadIconButton
              type="button"
              className="pq-file-action-btn"
              title="Download"
              onClick={() => triggerFileDownload(href, name, workDriveId)}
              size={28}
            />
          ) : null}
          <DeleteIconButton
            type="button"
            className="pq-file-action-btn"
            title="Delete file"
            onClick={() =>
              openDeleteModal(
                "project",
                String(f.id || "").trim(),
                name
              )
            }
            size={28}
          />
        </div>
      </div>
    );
  };

  if (pageLoading && !project) {
    return (
      <div className="project-quote-page pq-page-shell p-5 text-center text-muted">
        Loading project…
      </div>
    );
  }

  if (loadError && !project) {
    return (
      <div className="project-quote-page pq-page-shell p-5">
        <p className="text-danger mb-3">{loadError}</p>
        <button type="button" className="btn btn-outline-primary" onClick={() => navigate(projectsListPath)}>
          Back to projects
        </button>
      </div>
    );
  }

  return (
    <div className="project-quote-page pq-page-shell">
      <div className="pq-view-head" >
        <div>
          <div className="pq-view-title-row">
            <button
              className="border pq-back-link px-3 py-2"
              type="button"
              onClick={() => navigate(projectsListPath)}
            >
              <i className="ri-arrow-left-line" />
              <span>Back</span>
            </button>

            <div className="pq-ref-divider" />

            <h1 className="pq-view-title">Project Details</h1>

            <span className={statusPillClass}>{statusDisplay(project?.status)}</span>
          </div>

          <p className="pq-page-subtitle d-flex align-items-center flex-wrap gap-2">
            Project ID: <strong className="ms-1">{project?.project_number || projectId || "—"}</strong>
            <BrokerageAssociationStatusBadge source={project} />
          </p>
        </div>

        <div className="pq-actions-wrap" ref={actionsMenuWrapRef}>
          <button
            className="pq-btn pq-btn-outline pq-ref-actions"
            type="button"
            onClick={() => setShowActions((s) => !s)}
          >
            <i className="ri-more-2-fill" />
            <span>Actions</span>
            <i className="ri-arrow-down-s-line" />
          </button>

          {showActions && (
            <div className="pq-actions-menu">
              <button
                type="button"
                onClick={() => {
                  setShowActions(false);
                  setShowReopenProjectModal(true);
                }}
                disabled={isReopeningProject}
              >
                <i className="ri-refresh-line" />
                Reopen
              </button>

              <button
                type="button"
                onClick={() => projectId && navigate(`${projectsEditBase}/edit/${projectId}`)}
              >
                <i className="ri-edit-line" />
                Edit
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="pq-layout-two-col">
        <div className="pq-main-col">
          <div className="pq-card pq-section-card">
            <div className="pq-card-head pq-ref-card-head-blue pq-card-head-with-action">
              <h3 className="pq-ref-head-title">
                <i className="ri-user-line" />
                {endUserPartyInfo.cardTitle}
              </h3>
              <RefreshIconButton
                ariaLabel={endUserPartyInfo.refreshAriaLabel}
                title={endUserPartyInfo.refreshAriaLabel}
                onClick={handleRefreshAgentInfo}
              />
            </div>

            <div className="pq-card-body">
              <div className="pq-agent-grid-top">
                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon user">
                    <i className="ri-user-line" />
                  </span>
                  <div>
                    <div className="pq-k">{agentOrTeamNameLabel}</div>
                    <div className="pq-v">{agentOrTeamNameValue}</div>
                  </div>
                </div>

                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon email">
                    <i className="ri-mail-line" />
                  </span>
                  <div>
                    <div className="pq-k">Email</div>
                    <div className="pq-v">{project?.customer_email || "—"}</div>
                  </div>
                </div>

                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon phone">
                    <i className="ri-phone-line" />
                  </span>
                  <div>
                    <div className="pq-k">Phone</div>
                    <div className="pq-v">{projectPhone}</div>
                  </div>
                </div>

                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon brokerage">
                    <i className="ri-briefcase-4-line" />
                  </span>
                  <div>
                    <div className="pq-k">Brokerage</div>
                    <div className="pq-v">{project?.brokerage_firm_name || "—"}</div>
                  </div>
                </div>
                {showPartyOfficeName ? (
                  <div className="pq-agent-item">
                    <span className="pq-agent-item-icon brokerage">
                      <i className="ri-building-line" />
                    </span>
                    <div>
                      <div className="pq-k">Office Name</div>
                      <div className="pq-v">{partyOfficeName || "—"}</div>
                    </div>
                  </div>
                ) : null}
              </div>
              {/* <div className="pq-split-line mb-0" />
              <div className="row g-0 justify-content-center pq-additional-details-row">
                <button
                  className="pq-additional-btn my-3"
                  type="button"
                  onClick={() => setShowAgentDetails((s) => !s)}
                >
                  <i className={showAgentDetails ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                  {showAgentDetails ? "Hide Additional Details" : "Show Additional Details"}
                </button>
              </div> */}

              {/* {showAgentDetails && (
                <div className="pq-expand-details">
                  <div className="pq-split-line" />

                  <div className="pq-agent-grid-bottom">
                    <div>
                      <div className="pq-k">First Name</div>
                      <div className="pq-v">{agentNameParts.first}</div>
                    </div>

                    <div>
                      <div className="pq-k">Last Name</div>
                      <div className="pq-v">{agentNameParts.last}</div>
                    </div>

                    <div>
                      <div className="pq-k">Office Name</div>
                      <div className="pq-v">
                        {project?.office?.name || project?.office?.officeName || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="pq-split-line mt-3" />

                  <div className="pq-address-heading">
                    <i className="ri-map-pin-line" aria-hidden />
                    <span>Address Details</span>
                  </div>

                  <div className="pq-agent-address-section px-4">
                    <div className="pq-agent-address-stack">
                      <div className="pq-agent-address-field">
                        <div className="pq-k">Address Line 1</div>
                        <div className="pq-v">{displayAddress.addressLine1 || "—"}</div>
                      </div>
                      <div className="pq-agent-address-field">
                        <div className="pq-k">Address Line 2</div>
                        <div className="pq-v">{displayAddress.addressLine2 || "—"}</div>
                      </div>
                    </div>
                    <div className="pq-agent-address-row pq-agent-address-row--city-state-zip">
                      <div className="pq-agent-address-field">
                        <div className="pq-k">City</div>
                        <div className="pq-v">{displayAddress.city || "—"}</div>
                      </div>
                      <div className="pq-agent-address-field">
                        <div className="pq-k">State</div>
                        <div className="pq-v">{displayAddress.state || "—"}</div>
                      </div>
                      <div className="pq-agent-address-field">
                        <div className="pq-k">Zip Code</div>
                        <div className="pq-v">{displayAddress.zip || "—"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          </div>

          <div className="pq-card pq-section-card">
            <div className="pq-card-head pq-ref-card-head-indigo">
              <h3 className="pq-ref-head-title">
                <i className="ri-file-list-3-line" />
                Request Details
              </h3>
            </div>

            <div className="pq-card-body">
              <div className="pq-k">Project Title</div>
              <div className="pq-v pq-block-value">{project?.title || "—"}</div>

              <div className="pq-split-line" />

              <div className="pq-k">Design Request Notes (Internal)</div>
              <div className="pq-v pq-block-value">{project?.notes || "—"}</div>

              <div className="pq-split-line" />

              <div className="pq-meta-row">
                <div>
                  <div className="pq-k">Assigned Designer</div>
                  <div className="pq-v">{project?.designer_name || "—"}</div>
                </div>

                <div>
                  <div className="pq-k">Created Date</div>
                  <div className="pq-v">{formatDateTime(project?.created_at)}</div>
                </div>

                <div>
                  <div className="pq-k">Last Updated</div>
                  <div className="pq-v">{formatDateTime(project?.updated_at)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="pq-card pq-section-card">
            <div className="pq-conversation-card-body">
              <div className="pq-tabs-with-right">
                <button
                  type="button"
                  className={`pq-tab ${tab === "conversation" ? "active" : ""}`}
                  onClick={() => setTab("conversation")}
                >
                  <i className="ri-chat-4-line" />
                  Conversation
                </button>

                <button
                  type="button"
                  className={`pq-tab ${tab === "files" ? "active" : ""}`}
                  onClick={() => setTab("files")}
                >
                  <i className="ri-file-3-line" />
                  Shared Files <span className="pq-tab-badge">{sharedFileCount}</span>
                </button>

                <button
                  type="button"
                  className={`pq-mute-btn${conversationEndUserEmailMuted ? " pq-mute-btn-active" : ""}`}
                  title={
                    conversationEndUserEmailMuted
                      ? "Unmute: notify the customer by email when you send messages."
                      : "Mute: do not email the customer when you send messages."
                  }
                  onClick={() => void handleToggleClientMessageMute()}
                >
                  <i
                    className={
                      conversationEndUserEmailMuted ? "ri-notification-off-line" : "ri-notification-3-line"
                    }
                    aria-hidden
                  />
                  {conversationEndUserEmailMuted ? "Muted" : "Mute"}
                </button>
              </div>

              {tab === "conversation" ? (
                <>
                  <div
                    className="pq-convo-list"
                    ref={convoListRef}
                    onScroll={() => {
                      const listEl = convoListRef.current;
                      if (!listEl) return;
                      if (listEl.scrollTop <= 40) {
                        void loadOlderMessages();
                      }
                    }}
                  >
                    {isLoadingOlderMessages ? (
                      <p className="text-muted small px-2 py-1 mb-2">Loading older messages...</p>
                    ) : null}
                    {messages.length === 0 ? (
                      <p className="text-muted px-2 py-4 mb-0">No messages yet.</p>
                    ) : (
                      messages.map((message) => (
                        <div
                          className={`pq-convo-item${replyToMessageId && replyToMessageId === message.id
                            ? " pq-convo-item--reply-target"
                            : ""
                            }`}
                          key={message.id}
                        >
                          <div className="pq-convo-top">
                            <div className="pq-convo-user">
                              <span
                                className={`pq-convo-avatar ${message.role === "Admin" ? "designer" : "agent"
                                  }`}
                              >
                                <i className="ri-user-line" />
                              </span>

                              <div className="pq-convo-user-meta">
                                <strong>{message.sender}</strong>
                                {message.role ? (
                                  <span className="pq-convo-role">{message.role}</span>
                                ) : null}
                              </div>
                            </div>

                            <div className="pq-convo-top-actions">
                              <span className="pq-convo-time">{message.time}</span>
                              <button
                                type="button"
                                className="pq-reply-btn"
                                aria-label={`Reply to ${message.sender}`}
                                title="Reply to this message"
                                onClick={() => {
                                  setReplyToMessageId(message.id);
                                  requestAnimationFrame(() => {
                                    messageInputRef.current?.focus();
                                  });
                                }}
                              >
                                <i className="ri-reply-line" aria-hidden />
                                <span className="pq-reply-btn-text">Reply</span>
                              </button>
                            </div>
                          </div>

                          <div className="pq-convo-bubble-wrap">
                            {message.replyToMessageId && messagesById.get(message.replyToMessageId) ? (
                              <div className="pq-reply-inline">
                                <strong>{messagesById.get(message.replyToMessageId)?.sender}</strong>
                                <span>
                                  {messagesById.get(message.replyToMessageId)?.text || "(attachment)"}
                                </span>
                              </div>
                            ) : null}

                            <div
                              className={`pq-convo-bubble-body ${message.role === "Admin"
                                ? "pq-convo-bubble-body--admin"
                                : "pq-convo-bubble-body--agent"
                                }`}
                            >
                              <p className="pq-convo-text">{message.text}</p>

                              {(() => {
                                const proofFiles = message.replyFiles.filter((f) => f.isProof);
                                const otherReplyFiles = message.replyFiles.filter((f) => !f.isProof);
                                const showProofBlock = proofFiles.length > 0;
                                const proofVersionFromMessage = Number(message.proofVersion ?? 0);
                                const proofApproved =
                                  Number.isFinite(proofVersionFromMessage) &&
                                  approvedVersionNumbers.has(proofVersionFromMessage);
                                const showApprovedProofCardForMessage =
                                  proofApproved &&
                                  firstApprovedMessageIdByVersion.get(proofVersionFromMessage) === message.id;
                                const proofMeta = approvedProofMetaByVersion.get(
                                  Number(message.proofVersion ?? 0)
                                );
                                const proofGroup = proofVersionGroups.find(
                                  (g) => g.version === proofVersionFromMessage
                                );
                                const groupedProofFiles = (proofGroup?.files || []).map((f: any) => ({
                                  id: String(f?.id || f?._id || f?.project_file_id || f?.file_id || "").trim(),
                                  apiFileId: String(f?.file_id || "").trim() || undefined,
                                  name: String(f?.original_name || f?.file_name || "File"),
                                  previewUrl: String(f?.preview_url || f?.file_url || "").trim(),
                                  fileType: String(f?.file_type || "").trim() || undefined,
                                  isProof: true,
                                  version: Number(f?.version || proofVersionFromMessage),
                                  fileSize: Number.isFinite(Number(f?.file_size)) ? Number(f?.file_size) : undefined,
                                  quantity: Number.isFinite(Number(f?.quantity))
                                    ? Math.max(0, Math.floor(Number(f?.quantity)))
                                    : 0,
                                }));
                                return (
                                  <>
                                    {showProofBlock && showApprovedProofCardForMessage ? (
                                      <ApprovedProofCard
                                        version={Number(message.proofVersion ?? 1)}
                                        fileCount={groupedProofFiles.length || proofFiles.length}
                                        attribution={renderProofAttribution(project)}
                                        files={(groupedProofFiles.length > 0 ? groupedProofFiles : proofFiles).map((f) => ({
                                          id: String(f.id || f.apiFileId || "").trim(),
                                          name: f.name,
                                        }))}
                                        fileQuantities={proofMeta?.fileQuantities || []}
                                        showQtyPerFileDetails={false}
                                        onEdit={
                                          Number.isFinite(proofVersionFromMessage) && proofVersionFromMessage > 0
                                            ? () => void handleEditApprovedProofVersion(proofVersionFromMessage)
                                            : undefined
                                        }
                                        billingAddress={proofMeta?.billingAddress ?? (project as any)?.billing_address}
                                        shippingAddress={proofMeta?.shippingAddress ?? (project as any)?.shipping_address}
                                        poNumber={
                                          proofMeta?.poNumber ??
                                          (project as any)?.po_number ??
                                          (project as any)?.poNumber ??
                                          ""
                                        }
                                        quantity={Number(proofMeta?.quantity ?? (project as any)?.quantity ?? 1)}
                                        shippingMethod={proofMeta?.shippingMethod ?? (project as any)?.shipping_method}
                                        orderSummary={proofMeta?.orderSummary}
                                        orderSummarySaving={
                                          orderSummarySavingVersion === proofVersionFromMessage
                                        }
                                        onSaveOrderSummary={(text) =>
                                          handleSaveApprovedProofOrderSummary(proofVersionFromMessage, text)
                                        }
                                        filesContent={(groupedProofFiles.length > 0 ? groupedProofFiles : proofFiles).map((f, i) =>
                                          renderProofConversationRow(f, i, message)
                                        )}
                                      />
                                    ) : null}

                                    {(() => {
                                      const pendingProofSimple =
                                        showProofBlock && !proofApproved ? proofFiles : [];
                                      const showAttachmentGrid =
                                        pendingProofSimple.length > 0 ||
                                        otherReplyFiles.length > 0 ||
                                        message.attachments.length > 0;
                                      if (!showAttachmentGrid) return null;
                                      return (
                                        <div
                                          className={
                                            pendingProofSimple.length > 0
                                              ? "pq-attachments pq-attachments--proof-pending"
                                              : "pq-attachments"
                                          }
                                        >
                                          {pendingProofSimple.map((f) =>
                                            renderReplyFile(f, message.time)
                                          )}
                                          {otherReplyFiles.map((f) =>
                                            renderReplyFile(f, message.time)
                                          )}
                                          {message.attachments.map((file) =>
                                            renderAttachment(file)
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="pq-convo-input-wrap">
                    <div className="pq-split-line mt-3"></div>
                    <div>

                      {replyingToMessage ? (
                        <div className="pq-composer-section pq-composer-section--reply">
                          <div className="pq-replying-bar">
                            <div className="pq-replying-meta">
                              <strong>Replying to {replyingToMessage.sender}</strong>
                              <span>{replyingToMessage.text || "(attachment)"}</span>
                            </div>

                            <button
                              type="button"
                              className="pq-replying-cancel"
                              onClick={() => setReplyToMessageId("")}
                              aria-label="Cancel reply"
                            >
                              <i className="ri-close-line" />
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div className="pq-composer-border-wrap">
                        <div className="pq-composer-section pq-composer-section--text">
                          <textarea
                            ref={messageInputRef}
                            className="pq-msg-input"
                            placeholder="Type your message to the agent..."
                            value={messageDraft}
                            onChange={(e) => setMessageDraft(e.target.value)}
                          />
                        </div>

                        {hasComposerAttachments ? (
                          <div className="pq-composer-section pq-composer-section--attachments">
                            <div className="pq-attached-list">
                              {attachedDraftFiles.map(({ id, file }) => (
                                <div key={id} className="pq-attached-pill">
                                  <i className="ri-file-2-line" />
                                  <span className="pq-attached-pill-name" title={file.name}>
                                    {truncateFileName(file.name)}
                                  </span>
                                  <span className="pq-attached-pill-size">
                                    {(file.size / 1024).toFixed(2)} KB
                                  </span>
                                  <button
                                    type="button"
                                    className="pq-attached-pill-remove"
                                    onClick={() => removeDraftAttachment(id)}
                                    aria-label={`Remove ${file.name}`}
                                  >
                                    <i className="ri-close-line" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        <div className="pq-msg-actions--composer">
                          <div className="pq-msg-toolbar-flow">
                            <label className="pq-attach-label">
                              <i className="ri-attachment-2" />
                              Attach
                              <input
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                  appendDraftFiles(e.target.files);
                                  e.currentTarget.value = "";
                                }}
                              />
                            </label>

                            {hasComposerAttachments ? (
                              <span className="pq-msg-toolbar-divider pq-msg-toolbar-divider--vertical" aria-hidden />
                            ) : null}

                            {hasComposerAttachments ? (
                              <div className="pq-design-proof-block">
                                <span className="pq-design-proof-label">
                                  Design Proof?
                                  <span className="text-danger"> *</span>
                                </span>

                                <div className="pq-design-proof-toggles">
                                  <button
                                    type="button"
                                    className={`pq-toggle-yesno ${designProofChoice === "yes" ? "active" : ""}`}
                                    onClick={() => setDesignProofChoice("yes")}
                                  >
                                    Yes
                                  </button>

                                  <button
                                    type="button"
                                    className={`pq-toggle-yesno ${designProofChoice === "no" ? "active" : ""}`}
                                    onClick={() => setDesignProofChoice("no")}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            ) : null}

                            <div className="pq-emoji-picker-wrap">
                              <button
                                ref={emojiButtonRef}
                                type="button"
                                className="pq-emoji-inline-btn-proof"
                                aria-label="Add emoji"
                                onClick={() => setShowEmojiPicker((s) => !s)}
                              >
                                <i className="ri-emotion-line" />
                              </button>

                              {showEmojiPicker ? (
                                <div ref={emojiPickerRef} className="pq-emoji-picker-pop" role="dialog">
                                  {COMPOSER_EMOJIS.map((emoji) => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      className="pq-emoji-pick-btn"
                                      onClick={() => handleInsertEmoji(emoji)}
                                      aria-label={`Insert ${emoji}`}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            {hasComposerAttachments ? (
                              <span className="pq-attached-count-text">
                                {attachedDraftFiles.length} file
                                {attachedDraftFiles.length !== 1 ? "s" : ""} attached
                              </span>
                            ) : null}
                          </div>

                          <button
                            className="pq-send-btn"
                            type="button"
                            disabled={!canSendMessage}
                            onClick={handleSendMessage}
                            title={
                              hasComposerAttachments && designProofChoice === null
                                ? "Select Yes or No for Design Proof"
                                : undefined
                            }
                          >
                            <i className="ri-send-plane-fill" />
                            Send
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="pq-shared-files">
                  {proofVersionGroups.map(({ version, files }) => {
                    const groupApproved = approvedVersionNumbers.has(version);
                    const proofMeta = approvedProofMetaByVersion.get(version);

                    return groupApproved ? (
                      <div className="pq-proof-group pq-proof-group--proof-chat" key={`proof-v-${version}`}>
                        <div className="pq-msg-proof-approved pq-msg-proof-approved--embedded">
                          <ApprovedProofCard
                            version={version}
                            fileCount={files.length}
                            attribution={renderProofAttribution(project, (project?.agent_name || "").trim())}
                            files={files.map((f: any) => ({
                              id: String(f?.id || f?._id || f?.project_file_id || f?.file_id || "").trim(),
                              name: String(f?.original_name || f?.file_name || "File"),
                              quantity: Number.isFinite(Number((f as any)?.quantity))
                                ? Math.max(0, Math.floor(Number((f as any)?.quantity)))
                                : 0,
                            }))}
                            fileQuantities={proofMeta?.fileQuantities || []}
                            showQtyPerFileDetails={false}
                            onEdit={() => void handleEditApprovedProofVersion(version)}
                            billingAddress={proofMeta?.billingAddress ?? (project as any)?.billing_address}
                            shippingAddress={proofMeta?.shippingAddress ?? (project as any)?.shipping_address}
                            poNumber={
                              proofMeta?.poNumber ??
                              (project as any)?.po_number ??
                              (project as any)?.poNumber ??
                              ""
                            }
                            quantity={Number(proofMeta?.quantity ?? (project as any)?.quantity ?? 1)}
                            shippingMethod={proofMeta?.shippingMethod ?? (project as any)?.shipping_method}
                            orderSummary={proofMeta?.orderSummary}
                            orderSummarySaving={orderSummarySavingVersion === version}
                            onSaveOrderSummary={(text) => handleSaveApprovedProofOrderSummary(version, text)}
                            filesContent={files.map((f, i) => renderSharedProofFileRow(f, version, i))}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="pq-proof-group w-60" key={`proof-v-${version}`}>
                        <div className="pq-proof-group-head pq-proof-group-head--proof-pending">
                          <span className="pq-proof-group-head-proof-label">
                            <i className="ri-file-list-3-line" aria-hidden />
                            <span className="pq-proof-chip">Proof V{version}</span>
                            <span className="pq-proof-group-head-count">
                              {files.length} file{files.length !== 1 ? "s" : ""}
                            </span>
                          </span>
                        </div>
                        {files.map((f) => renderSharedProofFileRowPending(f))}
                      </div>
                    );
                  })}

                  {otherSharedFiles.length > 0 ? (
                    <div className="pq-proof-group">
                      <div className="pq-proof-group-head plain">OTHER FILES</div>

                      {otherSharedFiles.map((f) => {
                        const name = f.original_name || f.file_name || "—";
                        const workDriveId = String(f.file_id ?? "").trim();
                        const href = resolveProjectFileDownloadUrl({
                          file_id: workDriveId,
                          file_url: f.file_url,
                        });
                        const previewUrl = String(f.preview_url || "").trim();
                        const canImgPreview =
                          isLikelyImagePreview(f.file_type, name) && isHttpUrl(previewUrl);
                        const effectiveHref = href || (isHttpUrl(previewUrl) ? previewUrl : "");
                        const byInfo = resolveSharedFileUploaderDisplay(f.created_by, project);
                        const by = byInfo.name;
                        const byRole = byInfo.role;

                        const openOtherPreview = () => {
                          if (!effectiveHref && !previewUrl) return;
                          setFilePreview({
                            name,
                            previewUrl: canImgPreview ? previewUrl : "",
                            href: effectiveHref || previewUrl,
                            apiFileId: workDriveId || undefined,
                            sizeLabel: formatFileSizeForUi(f.file_size),
                            dateLabel: formatDateTime(f.created_at),
                          });
                        };

                        return (
                          <div className="pq-file-row" key={f._id}>
                            <div className={`pq-file-type ${fileTypeLabel(f.file_type, name).toLowerCase()}`}>
                              {fileTypeLabel(f.file_type, name)}
                            </div>

                            <div className="pq-file-meta">
                              <div className="pq-file-name">{name}</div>
                              <div className="pq-file-sub">
                                <span className="pq-file-sub-name">{by}</span>
                                {byRole ? <span className="pq-file-sub-role">{byRole}</span> : null}
                                <span className="pq-file-sub-time">{formatDateTime(f.created_at)}</span>
                              </div>
                            </div>

                            <div className="pq-file-actions">
                              {(canImgPreview || effectiveHref) && (
                                <ViewIconButton
                                  type="button"
                                  className="pq-file-action-btn"
                                  title="View"
                                  onClick={openOtherPreview}
                                  size={28}
                                />
                              )}
                              {href ? (
                                <DownloadIconButton
                                  type="button"
                                  className="pq-file-action-btn"
                                  title="Download"
                                  onClick={() =>
                                    triggerFileDownload(href, name, workDriveId)
                                  }
                                  size={28}
                                />
                              ) : null}
                              <DeleteIconButton
                                type="button"
                                className="pq-file-action-btn"
                                onClick={() => openDeleteModal("hard", workDriveId, name)}
                                title="Delete file"
                                size={28}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {proofVersionGroups.length === 0 && otherSharedFiles.length === 0 ? (
                    <p className="text-muted py-4 mb-0">No shared files yet.</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pq-card pq-sticky-side">
          <div className="pq-card-head pq-ref-card-head-indigo">
            <h3 className="pq-ref-head-title">
              <i className="ri-time-line" />
              Project Workflow
            </h3>
          </div>

          <div className="pq-card-body pq-sidebar-body">
            <ul className="pq-workflow-timeline pq-workflow-timeline--v2">
              {workflowStages.map((stage) => (
                <li
                  key={stage.key}
                  className={`pq-workflow-item pq-wf-connector-${stage.lineAfter}`}
                >
                  <span className={`pq-workflow-dot ${stage.dotClass}`}>
                    <i className={stage.iconClass} />
                  </span>
                  <div className="pq-wf-body">
                    <div className={`pq-wf-title ${stage.titleMuted ? "pq-wf-title--muted" : ""}`}>
                      {stage.title}
                    </div>
                    <div className={`pq-wf-sub ${stage.subClass}`}>{stage.statusLabel}</div>
                    {stage.extra ? <div className="pq-wf-extra">{stage.extra}</div> : null}
                    {stage.linkedQuote ? (
                      <div className="pq-wf-link-row">
                        <span>Linked Quote:</span>
                        {linkedOrderViewPath ? (
                          <Link to={linkedOrderViewPath} className="pq-linked-quote-btn">
                            {stage.linkedQuote}
                          </Link>
                        ) : (
                          <span>{stage.linkedQuote}</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            {canShowQuickActions ? (
              <div className="pq-side-section">
                <h5 className="pq-side-title pq-side-title--quick-actions">
                  <i className="ri-flashlight-line" aria-hidden />
                  Quick Actions
                </h5>

                {canShowAssignForQuote ? (
                  <div className="pq-side-box p-0 mb-3">
                    <div className="pq-side-box-title">
                      <i className="ri-user-settings-line" />
                      ASSIGN FOR QUOTE
                    </div>
                    <div className="p-3">
                      <div className="d-flex align-items-center gap-2">
                        <div>
                          <AsyncPaginate
                            key={signageFirmID || "firm"}
                            classNamePrefix="rs"
                            placeholder="Search Assigned To..."
                            value={selectedQuoteAssigneeOption}
                            loadOptions={loadQuoteAssigneeOptions}
                            onChange={(opt) =>
                              setSelectedQuoteAssigneeOption(opt as SignageDesignerSelectOption | null)
                            }
                            additional={{ page: 1 }}
                            debounceTimeout={300}
                            defaultOptions
                            isClearable
                            components={{ DropdownIndicator }}
                            formatOptionLabel={renderSignagePersonOptionLabel}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                            menuPosition="fixed"
                            // Keep portal/menu above cards while preserving shared select chrome.
                            styles={{
                              ...customSelectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary table-addbutton"
                          style={{ minWidth: 84 }}
                          onClick={() => void handleSaveQuoteAssignee()}
                          disabled={isSavingQuoteAssignee || !quoteAssigneeMembershipId(selectedQuoteAssigneeOption)}
                        >
                          {isSavingQuoteAssignee ? "Saving..." : "Save"}
                        </button>
                      </div>
                      {selectedQuoteAssigneeOption?.label ? (
                        <div className="mt-2" style={{ color: "#16a34a", fontWeight: 600 }}>
                          <i className="ri-check-line" /> Assigned to {selectedQuoteAssigneeOption.label}
                          {selectedQuoteAssigneeOption.email ? (
                            <div style={{ marginTop: 2, fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                              {selectedQuoteAssigneeOption.email}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="pq-side-box pq-proof-version-card">
                  <div className="pq-proof-version-card-head">
                    <i className="ri-file-3-line" aria-hidden />
                    <span>SELECT PROOF VERSION</span>
                  </div>

                  {proofVersionGroups.length === 0 ? (
                    <p className="pq-proof-version-empty text-muted small mb-0">No proof versions yet.</p>
                  ) : (
                    <ul className="pq-proof-version-list" role="radiogroup" aria-label="Proof version">
                      {proofVersionGroups.map(({ version, files }) => {
                        const rowKey = `v-${version}`;
                        const isSelected = selectedProofKey === rowKey;
                        const isApproved = approvedVersionNumbers.has(version);

                        return (
                          <li key={rowKey} className="pq-proof-version-item">
                            <label
                              className={`pq-proof-version-label ${isSelected ? "is-selected" : ""} ${isApproved ? "is-approved" : ""
                                }`}
                            >
                              <span className="pq-proof-version-radio-cell">
                                {!isApproved ? (
                                  <>
                                    <input
                                      type="radio"
                                      name="proof"
                                      className="pq-proof-version-radio-input"
                                      checked={isSelected}
                                      disabled={isApproved}
                                      onChange={() => setSelectedProofKey(rowKey)}
                                    />
                                    <span className="pq-proof-version-radio-faux" aria-hidden />
                                  </>
                                ) : null}
                              </span>
                              <span className="pq-proof-version-body">
                                <span className="pq-proof-version-title-row">
                                  <span className="pq-proof-version-title">
                                    Proof V{version}
                                    {isApproved ? (
                                      <span style={{ color: "rgb(22, 163, 74)", fontWeight: 600 }}>Approved</span>
                                    ) : null}
                                    {isApproved ? (
                                      <button
                                        type="button"
                                        className="pq-proof-version-approved-tag pq-proof-version-approved-tag-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          void handleUnapproveProofVersion(version);
                                        }}
                                        disabled={unapprovingProofVersion === version}
                                        title="Click to unapprove this proof"
                                      >
                                        {/* <i className="ri-check-line" aria-hidden /> */}
                                        {unapprovingProofVersion === version ? "Unapproving..." : "Unapproved"}
                                      </button>
                                    ) : null}
                                  </span>
                                  <span className="pq-proof-version-file-count">
                                    {files.length} file{files.length !== 1 ? "s" : ""}
                                  </span>
                                </span>
                                <span className="pq-proof-version-chips">
                                  {files.map((pf) => (
                                    <span key={pf.id} className="pq-proof-version-chip" title={pf.original_name || pf.file_name}>
                                      <i className="ri-file-text-line" aria-hidden />
                                      <span className="pq-proof-version-chip-name">
                                        {pf.original_name || pf.file_name}
                                      </span>
                                    </span>
                                  ))}
                                </span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <button
                    className={`pq-proof-version-submit pq-btn pq-btn-outline pq-full-btn ${!selectedProofKey || isApprovingProof || selectedProofAlreadyApproved
                      ? "pq-btn-disabled"
                      : ""
                      }`}
                    type="button"
                    disabled={!selectedProofKey || isApprovingProof || selectedProofAlreadyApproved}
                    onClick={handleApproveSelectedProof}
                  >
                    <i className="ri-checkbox-circle-line" aria-hidden />
                    {isApprovingProof ? "Approving..." : "Mark as Approved"}
                  </button>
                </div>

                {canShowCreateQuote ? (
                  <div className="pq-side-box p-0">
                    <div className="pq-side-box-title">
                      <i className="ri-file-3-line" />
                      CREATE QUOTE <span className="pq-required">*</span>
                    </div>

                    <label className="pq-order-option d-flex justify-content-between">
                      <span className="pq-order-row">
                        <span className="pq-order-icon">
                          <i className="ri-shopping-cart-line" />
                        </span>
                        <span>
                          <strong>Sales Order</strong>
                          <small>Product / signage purchase</small>
                        </span>
                      </span>
                      <input
                        type="radio"
                        name="orderType"
                        checked={selectedOrderType === "sales"}
                        onChange={() => setSelectedOrderType("sales")}
                      />
                    </label>

                    <label className="pq-order-option d-flex justify-content-between">
                      <span className="pq-order-row">
                        <span className="pq-order-icon">
                          <i className="ri-service-line" />
                        </span>
                        <span>
                          <strong>Service Order</strong>
                          <small>Installation / service work</small>
                        </span>
                      </span>
                      <input
                        type="radio"
                        name="orderType"
                        checked={selectedOrderType === "service"}
                        onChange={() => setSelectedOrderType("service")}
                      />

                    </label>

                    <button
                      className={`btn btn-primary table-addbutton pq-full-btn ${!selectedOrderType || isSavingOrderType ? "pq-btn-disabled" : ""
                        }`}
                      type="button"
                      disabled={!selectedOrderType || isSavingOrderType}
                      onClick={handleSelectOrderType}
                    >
                      <i className="ri-file-line" />
                      {isSavingOrderType ? "Saving..." : "Select an order type"}
                    </button>

                    <button className="pq-btn pq-btn-outline pq-full-btn-cancel pq-cancel-btn" type="button">
                      <i className="ri-close-line" />
                      Cancel Request
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {
        filePreview ? (
          <div
            className="pq-file-preview-backdrop"
            role="presentation"
            onClick={() => setFilePreview(null)}
          >
            <div
              className="pq-file-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pq-file-preview-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pq-file-preview-head">
                <div className="pq-file-preview-title-wrap">
                  <i className="ri-file-2-line" aria-hidden />
                  <h2 id="pq-file-preview-title" className="pq-file-preview-title">
                    {filePreview.name}
                  </h2>
                </div>
                <button
                  type="button"
                  className="pq-file-preview-close"
                  aria-label="Close preview"
                  onClick={() => setFilePreview(null)}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="pq-file-preview-body">
                {filePreview.previewUrl ? (
                  <PqFilePreviewImage src={filePreview.previewUrl} alt="" />
                ) : (
                  <div className="pq-file-preview-fallback">
                    <i className="ri-file-line" aria-hidden />
                    <p>Preview not available for this file type.</p>
                  </div>
                )}
              </div>
              <div className="pq-file-preview-foot">
                <span className="pq-file-preview-meta">
                  {filePreview.sizeLabel} · {filePreview.dateLabel}
                </span>
                <button
                  type="button"
                  className="pq-file-preview-download"
                  onClick={() =>
                    triggerFileDownload(filePreview.href, filePreview.name, filePreview.apiFileId)
                  }
                >
                  <DownloadIcon size={18} strokeWidth={2} />
                  Download
                </button>
              </div>
            </div>
          </div>
        ) : null
      }

      {
        showReopenProjectModal ? (
          <div
            className="pq-reopen-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pq-reopen-project-title"
          >
            <div className="pq-reopen-modal">
              <div className="pq-reopen-modal__header">
                <div className="pq-reopen-modal__icon" aria-hidden>
                  <i className="ri-loop-left-line" />
                </div>
                <h2 id="pq-reopen-project-title" className="pq-reopen-modal__title">
                  Reopen Project
                </h2>
              </div>
              <p className="pq-reopen-modal__body">
                A quote already exists for this project. Reopening will continue the design discussion only.
                Please review the existing quote and cancel or update it if needed.
              </p>
              <div className="pq-reopen-modal__footer">
                <button
                  type="button"
                  className="pq-reopen-modal__btn pq-reopen-modal__btn--secondary"
                  onClick={() => setShowReopenProjectModal(false)}
                  disabled={isReopeningProject}
                >
                  Go Back
                </button>
                <button
                  type="button"
                  className="pq-reopen-modal__btn pq-reopen-modal__btn--primary"
                  onClick={() => void handleReopenProject()}
                  disabled={isReopeningProject}
                >
                  <i className="ri-loop-left-line" aria-hidden />
                  {isReopeningProject ? "Reopening..." : "Reopen Project"}
                </button>
              </div>
            </div>
          </div>
        ) : null
      }
      <BillingShippingApprovalModal
        open={showAddressPickerModal}
        loading={addressSubmitting || isApprovingProof}
        sameAsBilling={sameAsBilling}
        setSameAsBilling={setSameAsBilling}
        selectedBillingAddressId={selectedBillingAddressId}
        setSelectedBillingAddressId={setSelectedBillingAddressId}
        selectedShippingAddressId={selectedShippingAddressId}
        setSelectedShippingAddressId={setSelectedShippingAddressId}
        addressOptions={publicAddresses.map((addr) => ({
          value: pickAddressId(addr),
          label: formatAddressSingleLine(addr) || addr.label || "Address",
        }))}
        shippingMethods={SHIPPING_METHODS}
        selectedShippingMethodId={selectedShippingMethodId}
        setSelectedShippingMethodId={setSelectedShippingMethodId}
        proofFiles={selectedProofFilesForApproval}
        fileQuantities={selectedFileQuantities}
        setFileQuantity={(fileId, qty) => {
          setSelectedFileQuantities((prev) => ({ ...prev, [fileId]: qty }));
        }}
        poNumber={approvalPoNumber}
        setPoNumber={setApprovalPoNumber}
        confirmLabel={editingApprovedProofVersion ? "Save" : "Confirm & Approve Design"}
        onAddNewAddress={() => setShowAddressModal(true)}
        onClose={() => {
          if (addressSubmitting || isApprovingProof) return;
          setShowAddressPickerModal(false);
          setPendingApproveSelectedProof(false);
          setEditingApprovedProofVersion(null);
        }}
        onConfirm={() => void handleConfirmAddressAndApprove()}
      />
      <AddAddressModal
        open={showAddressModal}
        loading={addressSubmitting}
        title="Add New Address"
        subtitle="Fill in the details below"
        onClose={() => {
          if (addressSubmitting) return;
          setShowAddressModal(false);
        }}
        onSubmit={handleSubmitApprovalAddress}
      />
      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetName || "file"}
        onCancel={closeDeleteModal}
        onDelete={confirmDeleteFile}
        isDeleting={isDeletingFile}
      />
    </div >
  );
};

export default SignageViewprojectandquote;
